/**
 * Supply Chain Module - PSP/CRE Tool v3.0
 *
 * @module supplyChainModule
 * @version 5.1.0
 * @date March 9, 2026
 *
 * VERSION 5.3.0 (April 7, 2026) — F3-MRoE-STREAM2: Civilian patient blood demand correction.
 *   extractFromCalculatorOutput() Shape D now reads casualties.mascal_analysis.mroe_load.civilian_stream.
 *   civilian_stream.t1_surgical_daily and t2_ward_daily added to repDay and perDayArray T1/T2 counts.
 *   T3 (HNS routed) and EPW stream (separate chain, AJMedP-1 §3.21) excluded.
 *   Only applied when civilian_stream.competes_with_military === true (matrix-derived flag).
 *   V3.1: flat daily civilian load added uniformly. V4: phase-linked civilian load per phase.
 *
 * VERSION 5.2.0 CHANGES (March 15, 2026) — ENH-008: Drone UAS medical supply delivery:
 *   IMPORT: DRONE_DELIVERY_SPECS from classVIII.js v3.3.
 *   buildDailySimulation(): new DRONE branch — fires when r.delivery_method === 'DRONE'.
 *   Reads drone_type (default CARGO_DRONE) and ad_threat_override (default MEDIUM).
 *   CARGO_DRONE: 12 PRBC × availability × 0.95 cold-factor + 80 tourniquets × availability.
 *   LIGHT_DROP_DRONE: tourniquets only (no cold chain).
 *   AD availability from DRONE_DELIVERY_SPECS[type].ad_threat_availability.
 *   Cold chain losses recorded in coldChainLosses[] with source: 'drone_flight_variance'.
 *   Backward compatible: events without delivery_method === 'DRONE' unchanged.
 *
 * VERSION 5.1.0 CHANGES (March 9, 2026) — ACT1-SURGICAL-KITS:
 *   surgical_kits removed from simulation — US Army ATP 4-02.25 only, not NATO doctrine.
 *   - calculateDailyConsumption(): surgical_kits line removed. surgicalCasesPerDay retained
 *     for controlled_substances calculation (that IS consumed per DCS case).
 *   - getSupplyCategory(): surgical_kits removed from CONSUMABLES list.
 *   - normalizeSupplyStock() fieldMap comment: surgical_kits marked REMOVED FROM APP.
 *   - materielFieldMap in push_package branch: surgical_kits entry removed.
 *
 * VERSION 5.0.0 CHANGES (March 9, 2026) — BUG-RESUPPLY-001/002/003:
 *
 * BUG-RESUPPLY-004 FIX: Flat materiel items in push_package events now applied to simStock.
 *   Root cause: push_package branch read r.supplies.blood_products only. Flat keys in
 *   r.supplies (antibiotics_doses, tourniquets, iv_fluids_liters, dressings_sets, etc.)
 *   were silently ignored — SURGICAL_RESUPPLY packages had zero effect on materiel DOS.
 *   Fix: after blood-products handling, iterate r.supplies flat keys, apply fieldMap
 *   (mirrors normalizeSupplyStock()), scale by modifier, add to simStock[simKey] and
 *   accumulate truck weight.
 *   ACT1 update: surgical_kits removed from materielFieldMap (not a NATO doctrine item).
 *
 * BUG-RESUPPLY-001 FIX: Manual resupply events now apply force-scaled quantities.
 *   Root cause: manual Tab4 events store push_package + supplies.blood_products (base
 *   package contents, e.g. prbc_units=50) but no quantity_modifier. The simulation
 *   multiplied bp.prbc_units × (r.quantity_modifier || 1) = 50 × 1 = 50 units — 0.05 DOS
 *   for a brigade-sized force consuming ~1 000 units/day. Effectively invisible.
 *   Fix: when quantity_modifier is absent, derive force-scaled modifier from
 *   bloodBase / STANDARD_RESUPPLY_BASE_PRBC (same logic auto-schedule uses).
 *   This is package-change-proof: STANDARD_RESUPPLY_BASE_PRBC is the only coupling point.
 *
 * BUG-RESUPPLY-002 FIX: resupply_events now included in calculateLogisticsProjection output.
 *   Root cause: return object omitted resupply_events. Tab5 read
 *   logisticsProjection.resupply_events || [] → always [] → Events column always blank,
 *   resupply day rows never highlighted.
 *   Fix: add resupply_events: resupplySchedule to return object.
 *
 * BUG-RESUPPLY-003 FIX: Trucks column now shows per-day resupply delivery trucks.
 *   Root cause: trucks_required was calculateDailyWeightVolume(dayConsumption).trucks_required
 *   — trucks needed to carry that day's consumed materiel, constant ~1 across all days.
 *   Fix: accumulate resupply weight across all 4 event formats during the resupply loop.
 *   New field trucks_resupply added to each dailySim row: ceil(resupplyWeightKg /
 *   CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg). 0 on non-resupply days.
 *   Tab5 Trucks column updated to use trucks_resupply; trucks_required retained (consumption).
 *
 * VERSION 4.9.0 CHANGES (March 6, 2026) — ENH-014c: Cold Chain Viability in Simulation:
 *
 * ENH-014c-A: BLOOD_TRANSPORT_LIMITS imported from classVIII.js v2.9.
 * ENH-014c-B: BLOOD_ITEM_TO_LIMIT_KEY — maps BLOOD_ITEMS keys to BLOOD_TRANSPORT_LIMITS keys.
 * ENH-014c-C: computeColdChainViability(msrLegs) — binary viability factor per product.
 *   Worst-leg model: max(all leg one_way_hours) vs product max_hours.
 *   Factor 0 = exceeded (non-viable). Factor 1 = viable or no cold chain limit.
 *   Returns { factors: {prbc,ffp,platelets,cryo}, meta: {per-product diagnostics} }.
 * ENH-014c-D: buildDailySimulation() gains 6th param msrLegs (default null).
 *   All 4 resupply event formats apply coldChainFactors before simStock update.
 *   Losses tracked in coldChainLosses[]: { day, item, ordered, applied, factor, meta }.
 *   Return extended: { dailySim, stockoutDays, coldChainLosses }.
 * ENH-014c-E: calculateLogisticsProjection() gains 7th param msrLegs (default null).
 *   Passes msrLegs to buildDailySimulation(). Output extended:
 *     cold_chain_losses[]     — per-event degradation records (empty if no degradation)
 *     cold_chain_viability{}  — { factors, meta, any_non_viable }
 * Backward compatible: all new params default null; callers not passing msrLegs unaffected.
 *
 * VERSION 4.8.0 CHANGES (March 6, 2026) — ARCH-002: MSR Route Wiring:
 *
 * ARCH-002-A: Imports MSR_ROAD_SPEEDS, MSR_TERRAIN_MODIFIERS, MSR_SEASON_MODIFIERS,
 *   MSR_DEFAULTS from classVIII.js v2.8.
 *
 * ARCH-002-B: calculateConvoyTravelTime(msrConfig) — new EXPORTED pure function.
 *   Computes convoy travel time from depot to FMSS given road type, terrain, season.
 *   Returns: { distance_km, effective_speed_kmh, one_way_hours, round_trip_hours,
 *              lead_time_days, same_day_feasible, warning, warning_label, ... }
 *   lead_time_days = ceil(round_trip_hours / 24) — advance order notice in days.
 *   same_day_feasible = round_trip_hours <= 16h (NATO crew duty day limit).
 *
 * ARCH-002-C: calculateLogisticsProjection() gains optional 6th parameter: msrConfig.
 *   Output extended with: convoy_plan: calculateConvoyTravelTime(msrConfig) | null.
 *   Null when msrConfig not provided — backward compatible.
 *
 * VERSION 4.7.0 CHANGES (March 6, 2026) — ENH-010: Weight/Volume / Convoy Planning:
 *
 * ENH-010-A: Imports ITEM_WEIGHTS_KG, ITEM_VOLUMES_M3, CONVOY_CAPACITY from classVIII.js v2.7.
 *
 * ENH-010-B: calculateDailyWeightVolume(dailyConsumption) — new EXPORTED pure function.
 *   Takes the output object of calculateDailyConsumption() and returns:
 *   { total_weight_kg, total_volume_m3, blood_weight_kg, blood_volume_m3,
 *     materiel_weight_kg, materiel_volume_m3, trucks_required, trucks_weight, trucks_volume,
 *     by_item: { [item]: { weight_kg, volume_m3 } } }
 *   trucks_required = max(trucks_weight, trucks_volume) — binding constraint.
 *   Uses CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg/volume_m3 (80% planning factor).
 *
 * ENH-010-C: buildDailySimulation() extended — each dailySim row now includes:
 *   blood_weight_kg    — total blood product weight consumed that day (kg)
 *   materiel_weight_kg — total medical materiel weight consumed that day (kg)
 *   total_weight_kg    — combined (for quick convoy total)
 *   blood_volume_m3    — blood volume (m³) consumed that day
 *   materiel_volume_m3 — materiel volume (m³) consumed that day
 *   total_volume_m3    — combined volume
 *   trucks_required    — standard trucks needed for that day's resupply (max weight/volume)
 *
 * ENH-010-D: calculateLogisticsProjection() output extended with:
 *   daily_weight_volume: { total_weight_kg, total_volume_m3, trucks_required, ... }
 *   Computed from average daily_consumption (not per-day — use dailySim for per-day).
 *
 * VERSION 4.6.0 CHANGES (March 3, 2026) — PKG-001/002/003:
 *
 * PKG-001: generateSchedule() package selection now context-aware (APP_RULES 2.1).
 *   Previously always emitted push_package: 'STANDARD_RESUPPLY'.
 *   Now:
 *   - Blood-only critical stockout predictions → EMERGENCY_BLOOD
 *   - Blood alert RED or <safetyMargin days DOS → MASS_CASUALTY_PUSH
 *   - Multi-item / MASCAL scenario → MASS_CASUALTY_PUSH
 *   - Routine resupply → STANDARD_RESUPPLY
 *   Logic: _selectPackage() closure inside generateSchedule, reads
 *   stockoutAnalysis.predictions and scenario.medical_configuration.blood_config.
 *
 * PKG-002: STRATEGIES.targetDOS now resolved from THRESHOLD_PROFILES[nationalStandard]
 *   (resupplyThresholds.js) via _resolveTargetDOS(). Previously hardcoded 7/5/3 days
 *   (NATO only). Now:
 *   CONSERVATIVE → resupply.target_dos    (NATO:15d, Romanian:18d, US:12d)
 *   BALANCED     → resupply.routine_threshold (NATO:7d, Romanian:8d, US:6d)
 *   MINIMAL      → resupply.emergency_threshold (NATO:3d, Romanian:4d, US:2d)
 *   generateSchedule() accepts new 5th param: nationalStandard (default 'NATO').
 *   THRESHOLD_PROFILES, getThresholdProfile imported from resupplyThresholds.js.
 *
 * PKG-003: buildDailySimulation() Format 4 handler fixed — auto-generated events
 *   with supplies:{} were invisible to the DOS projection (0 blood added to simStock).
 *   Now: when push_package exists but blood_products absent/empty, derives unit
 *   contents via _unitsFromModifier(quantity_modifier) — same lookup generateSchedule
 *   uses internally. Also handles MASS_CASUALTY_PUSH whole_blood_collection_kits:
 *   each kit converts to 2 PRBC equivalent (NATO WBB planning factor, AJMedP-1).
 *
 * VERSION 4.5.0 CHANGES (March 3, 2026) — WTD-01: Wound-type-aware dressings:
 * - calculateDailyConsumption() accepts optional 3rd param: operationType (default null)
 * - When operationType provided: dressings_sets uses getWeightedDressingSets() from
 *   woundTypeDistribution.js (burns T1=8 sets vs extremity T1=6 sets per JTS JTTR)
 * - When operationType null: flat per-triage rates unchanged (v4.4.0 behaviour)
 * - Import: getWeightedDressingSets, WOUND_TYPE_DRESSING_RATE_DNBI from woundTypeDistribution.js
 * - Zero regression: all existing call sites use null default
 *
 * VERSION 4.4.0 CHANGES (March 2, 2026) — ISSUE-01 Supply Scaling Fix:
 * - Added dressings_sets to calculateDailyConsumption(): per-triage + DNBI fraction
 *   Source: TCCC CoTCCC 2024; DNBI_CATEGORIES wound-requiring fraction (dnbiRates.js)
 * - Added surgical_kits: (t1×0.8 + t2×0.3) × surgical_kit_per_case (1.0, nationalLogistics)
 *   Source: ATP 4-02.25 FST equipment tables (1 sterile pack per DCS procedure)
 *   [REMOVED in v5.1.0 — US Army ATP 4-02.25 only, not NATO doctrine]
 * - Added controlled_substances: surgical_cases × controlled_per_surgical (nationalLogistics)
 *   Source: NATO 8.0 doses/case; US 12.0 doses/case (already in nationalLogistics)
 * - Updated getSupplyCategory(): new items categorised correctly
 * - Updated normalizeStockKeys(): controlled_substances_doses → controlled_substances
 *
 * VERSION 4.0.0 CHANGES (February 26, 2026) — PSP_CRE_MASTER_FIX_PLAN_v2_0 Phase B:
 *
 * B-01: ARCH-004 — Stock formula corrected.
 *   WRONG (v3.3.0):  requiredStock = daily × (opDuration + targetMDOS)
 *   CORRECT (v4.0.0): requiredStock = daily × targetMDOS
 *   Commanded MDOS = STARTING STOCK ON DAY 1, not end-state reserve.
 *   A 7-day Commanded MDOS on a 10-day operation means the force starts
 *   with 7 days of supply and needs resupply by Day 7 or it stockouts.
 *   Same fix applied to calculateRequiredStock() — opDuration no longer
 *   included in formula (parameter retained for backward-compatible signature).
 *
 * B-02: dailyStockSimulation[] array added to calculateLogisticsProjection output.
 *   Each element: { day, blood_remaining, nato_medical_materiel_remaining, surgical_remaining }
 *   Values are expressed in days-of-supply remaining (float, 1 decimal).
 *
 * B-03: Simulation is phase-aware.
 *   If casualties input is an Array, each day uses its own daily casualty object
 *   (DNBI-only rates during movement phases, full battle rates during combat phases).
 *   If casualties input is a flat object, the same rate applies every day.
 *
 * B-04: Stockout detection added.
 *   stockout_days: { blood: N|null, nato_medical_materiel: N|null, surgical: null }
 *   Null = no stockout within operation duration.
 *
 * B-05: Resupply event processing in simulation.
 *   resupplySchedule events spike stock on their arrival day.
 *   Supports category-level and item-level resupply event formats.
 *
 * B-06: calculateLogisticsProjection() signature updated:
 *   (casualties, nationalStandard, opDuration, targetMDOS, resupplySchedule = [])
 *
 * B-07: Output shape updated — new fields added:
 *   commanded_mdos_days (null — Tab4 sets this),
 *   stockout_days: { blood, nato_medical_materiel, surgical },
 *   daily_simulation: Array of day-by-day DOS remaining.
 *
 * B-08: ESLint compliance — zero warnings.
 *
 * @version 3.3.0 CHANGES (February 26, 2026) — FIX_PLAN_LOGISTICS_DOS_v1_0 Phase L-2:
 * - L2-01: nationalStandard parameter added to calculateDailyConsumption()
 * - L2-02: calculateDailyConsumption() rewritten — reads from getNationalLogistics(nationalStandard)
 *          instead of classVIII CONSUMPTION_RATES directly. Enforces national rates everywhere.
 *          CONSUMPTION_RATES import removed (no longer read directly — APP_RULES unused-vars).
 *          CLASSVIII_TO_OUTPUT_KEY removed (unused after rewrite — replaced by named properties).
 * - L2-03: nationalStandard parameter added to calculateRequiredStock()
 * - L2-04: SUPERSEDED by B-01 above. Formula (opDuration + targetDOS) was architecturally wrong.
 * - L2-05: tourniquets, chest_seals, analgesics (morphine) now computed from nationalLogistics
 *          per-triage rates (tourniquet_per_t1/t2/t3, chest_seal_per_t1/t2/t3, morphine_per_t1/t2/t3)
 *          added in nationalLogistics.js v2.2.0 L1-06.
 * - L2-06: calculateLogisticsProjection() added as single Tab3 entry-point. NOW OWNED BY TAB4
 *          (ARCH-001/ARCH-009 — Tab3 call removed in Phase C).
 * - L2-07: Diagnostic console.log block removed.
 *
 * @description
 * Calculates daily supply consumption, tracks stock levels over operation duration,
 * projects days of supply remaining via day-by-day simulation, detects stockouts,
 * and generates G4 logistics requests.
 *
 * Issue IDs: CC-004, ARCH-004, ARCH-005
 * Priority: CRITICAL
 *
 * @requires constants/classVIII.js
 * @requires constants/nationalLogistics.js
 *
 * Doctrinal Reference:
 * - ATP 4-02.1: Army Medical Logistics
 * - AJMedP-1: Allied Joint Medical Planning Doctrine
 */

// ============================================================================
// IMPORTS
// SCM-01: DOS_THRESHOLDS, REQUEST_LEAD_TIMES, WASTAGE_FACTORS imported
// from classVIII.js — single source of truth (APP_RULES 2.1).
// Re-exported below to preserve any external import paths.
// ============================================================================

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';
import { THREAT_EFFECTS_USE, resolveRoadState, supplyDelivery } from '../../constants/threatEffects.js';
import { DRONE_THREAT } from '../../constants/threatAssessment.js';
import { resolveScenarioEnvironment } from '../scenarioEnvironment.js';

import { requireNumber } from '../requireValue.js';

import {
  DOS_THRESHOLDS,
  REQUEST_LEAD_TIMES,
  WASTAGE_FACTORS,
  // ENH-010: weight/volume constants for convoy planning
  ITEM_WEIGHTS_KG,
  ITEM_VOLUMES_M3,
  CONVOY_CAPACITY,
  // ARCH-002: MSR forward supply route constants
  MSR_ROAD_SPEEDS,
  MSR_TERRAIN_MODIFIERS,
  MSR_SEASON_MODIFIERS,
  MSR_DEFAULTS,
  // ENH-014c: blood product transport time limits for cold chain viability
  BLOOD_TRANSPORT_LIMITS,
  // ENH-008: drone UAS medical supply specs
  DRONE_DELIVERY_SPECS
  // CONSUMPTION_RATES removed in v3.3.0 (L2-02): calculateDailyConsumption now reads through
  // getNationalLogistics() — classVIII rates are no longer accessed directly.
} from '../../constants/classVIII';

// L2-02: National logistics rates — single source of truth for all consumption calculations.
// All dailyConsumption calls use getNationalLogistics(nationalStandard).consumption_rates,
// which deep-merges NATO baseline with national overrides (e.g. ROMANIAN prbc_per_t1 = 4.5).
import { getNationalLogistics } from '../../constants/nationalLogistics';

// WTD-01: Wound-type-aware dressing consumption.
// When operationType is provided, dressing rates use wound-type-weighted sets/casualty
// rather than flat per-triage rates (burns=8 sets/T1 vs extremity=6 sets/T1 per JTS JTTR).
import {
  getWeightedDressingSets,
  WOUND_TYPE_DRESSING_RATE_DNBI
} from '../../constants/woundTypeDistribution.js';

// PKG-002: National threshold profiles — target DOS values for auto-schedule strategies.
// caution_threshold → CONSERVATIVE (maintain this DOS), warning_threshold → BALANCED,
// critical_threshold → MINIMAL. Replaces hardcoded 7/5/3 in STRATEGIES.
import {
  getThresholdProfile,
  estimateDeliveryTime
} from '../../constants/resupplyThresholds';

// Re-export so modules importing these from supplyChainModule.js continue to work
export { DOS_THRESHOLDS, REQUEST_LEAD_TIMES, WASTAGE_FACTORS };

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Output key reference (documentation only — v3.3.0+)
 * calculateDailyConsumption() produces these output keys, used by normalizeStockKeys() and Tab4.
 * classVIII field → consumption output key:
 *   prbc_units        → prbc                  (blood)
 *   ffp_units         → ffp                   (blood)
 *   platelets_units   → platelets             (blood)
 *   whole_blood_units → whole_blood           (blood)
 *   antibiotics_doses → antibiotics           (pharma)
 *   morphine_doses    → analgesics            (pharma — morphine_per_t1/t2/t3 from nationalLogistics)
 *   iv_fluids_liters  → crystalloid           (IV)
 *   tourniquets       → tourniquets           (consumable)
 *   chest_seals       → chest_seals           (consumable)
 *   dressings_sets    → dressings_sets        (consumable — ISSUE-01 FIX: per-triage + DNBI)
 *   surgical_kits     → surgical_kits         (consumable — ISSUE-01 FIX: 1 per DCS case)
 *   controlled_substances_doses → controlled_substances (pharma — ISSUE-01 FIX: per DCS case)
 *
 * Blood category items for simulation:
 *   BLOOD_ITEMS = ['prbc', 'ffp', 'platelets']
 *
 * NATO Medical Materiel category items for simulation:
 *   MATERIEL_ITEMS = ['tourniquets', 'chest_seals', 'antibiotics', 'crystalloid', 'analgesics']
 */

// Category membership — used by simulation and by_category grouping
const BLOOD_ITEMS    = ['prbc', 'ffp', 'platelets'];
const MATERIEL_ITEMS = ['tourniquets', 'chest_seals', 'antibiotics', 'crystalloid', 'analgesics'];

// ============================================================================
// ENH-010: WEIGHT / VOLUME CALCULATION
// ============================================================================

/**
 * Calculate daily weight and volume requirements for convoy planning.
 *
 * Converts item quantities (from calculateDailyConsumption output) to kg and m³
 * using constants from classVIII.js (ITEM_WEIGHTS_KG, ITEM_VOLUMES_M3).
 * Returns truck count estimates using CONVOY_CAPACITY.STANDARD_TRUCK (80% planning factor).
 *
 * @param {Object} dailyConsumption - Output of calculateDailyConsumption() or any daily
 *   consumption object with the same item keys (prbc, ffp, crystalloid, etc.)
 * @returns {Object} Weight/volume breakdown:
 *   {
 *     total_weight_kg,  total_volume_m3,
 *     blood_weight_kg,  blood_volume_m3,
 *     materiel_weight_kg, materiel_volume_m3,
 *     trucks_weight,    trucks_volume,   trucks_required,  (all integers, ceil)
 *     by_item: { [item]: { quantity, weight_kg, volume_m3 } }
 *   }
 *
 * ENH-010-B
 */
export const calculateDailyWeightVolume = (dailyConsumption) => {
  if (!dailyConsumption || typeof dailyConsumption !== 'object') {
    throw new Error('[SupplyChain] calculateDailyWeightVolume: dailyConsumption is required');
  }

  const truck = CONVOY_CAPACITY.STANDARD_TRUCK;
  const byItem = {};

  let bloodWeightKg    = 0;
  let bloodVolumeM3    = 0;
  let materiелWeightKg = 0;
  let materiелVolumeM3 = 0;

  // Process every item that has a weight constant (all tracked items)
  for (const [item, weightPerUnit] of Object.entries(ITEM_WEIGHTS_KG)) {
    const qty = dailyConsumption[item];
    if (qty == null || qty === 0) continue;

    const volPerUnit = ITEM_VOLUMES_M3[item] || 0;
    const itemWeightKg = qty * weightPerUnit;
    const itemVolumeM3 = qty * volPerUnit;

    byItem[item] = {
      quantity:  qty,
      weight_kg: parseFloat(itemWeightKg.toFixed(2)),
      volume_m3: parseFloat(itemVolumeM3.toFixed(5)),
    };

    if (BLOOD_ITEMS.includes(item)) {
      bloodWeightKg += itemWeightKg;
      bloodVolumeM3 += itemVolumeM3;
    } else {
      materiелWeightKg += itemWeightKg;
      materiелVolumeM3 += itemVolumeM3;
    }
  }

  const totalWeightKg = bloodWeightKg + materiелWeightKg;
  const totalVolumeM3 = bloodVolumeM3 + materiелVolumeM3;

  // Truck count — limited by whichever constraint binds first (weight or volume)
  const trucksWeight = Math.ceil(totalWeightKg / truck.effective_payload_kg);
  const trucksVolume = Math.ceil(totalVolumeM3 / truck.effective_volume_m3);
  const trucksRequired = Math.max(trucksWeight, trucksVolume);

  return {
    total_weight_kg:    parseFloat(totalWeightKg.toFixed(1)),
    total_volume_m3:    parseFloat(totalVolumeM3.toFixed(3)),
    blood_weight_kg:    parseFloat(bloodWeightKg.toFixed(1)),
    blood_volume_m3:    parseFloat(bloodVolumeM3.toFixed(3)),
    materiel_weight_kg: parseFloat(materiелWeightKg.toFixed(1)),
    materiel_volume_m3: parseFloat(materiелVolumeM3.toFixed(3)),
    trucks_weight:      trucksWeight,
    trucks_volume:      trucksVolume,
    trucks_required:    trucksRequired,
    by_item:            byItem,
  };
};

/**
 * Calculate convoy travel time and lead time requirements for a given MSR configuration.
 *
 * Purpose: Given depot-to-FMSS distance and route conditions, compute how long a
 * resupply convoy takes and how many days' advance ordering is required.
 *
 * Formula: effective_speed = base_speed × terrain_modifier × season_modifier
 *          one_way_hours   = distance_km / effective_speed_kmh
 *          round_trip_hours = one_way_hours × 2 (unloading time adds ~2h included below)
 *          lead_time_days   = ceil(round_trip_hours / 24) — minimum advance order notice
 *
 * @param {Object} msrConfig - MSR configuration (from Tab4 Section 4F or MSR_DEFAULTS)
 *   {
 *     distance_km:   number  (depot to FMSS, one-way)
 *     road_type:     string  (key of MSR_ROAD_SPEEDS)
 *     terrain:       string  (key of MSR_TERRAIN_MODIFIERS)
 *     season:        string  (key of MSR_SEASON_MODIFIERS)
 *   }
 * @returns {Object} Convoy plan:
 *   {
 *     distance_km, road_type_label, terrain_label, season_label,
 *     base_speed_kmh, effective_speed_kmh,
 *     one_way_hours, round_trip_hours,
 *     lead_time_days,          — advance order notice (days)
 *     same_day_feasible,       — boolean: round-trip < 16h (crew duty day)
 *     warning,                 — null | 'SLOW_ROUTE' | 'MULTI_DAY' | 'EXTREME'
 *     warning_label            — human-readable warning string or null
 *   }
 *
 * ARCH-002
 */
export const calculateConvoyTravelTime = (msrConfig = {}) => {
  const cfg = { ...MSR_DEFAULTS, ...msrConfig };

  const roadSpec    = MSR_ROAD_SPEEDS[cfg.road_type]          || MSR_ROAD_SPEEDS[MSR_DEFAULTS.road_type];
  const terrainSpec = MSR_TERRAIN_MODIFIERS[cfg.terrain]       || MSR_TERRAIN_MODIFIERS[MSR_DEFAULTS.terrain];
  const seasonSpec  = MSR_SEASON_MODIFIERS[cfg.season]         || MSR_SEASON_MODIFIERS[MSR_DEFAULTS.season];

  const baseSpeed      = roadSpec.speed_kmh;
  const effectiveSpeed = parseFloat((baseSpeed * terrainSpec.modifier * seasonSpec.modifier).toFixed(1));

  if (effectiveSpeed <= 0) {
    throw new Error('[SupplyChain] calculateConvoyTravelTime: effective speed is zero — check MSR constants');
  }

  const distKm         = cfg.distance_km;
  const oneWayHours    = parseFloat((distKm / effectiveSpeed).toFixed(2));
  // Round-trip: 2× one-way + 2h for unloading/vehicle checks
  const roundTripHours = parseFloat((oneWayHours * 2 + 2).toFixed(2));
  // Lead time: how many full days before resupply day must the order be placed
  const leadTimeDays   = Math.ceil(roundTripHours / 24);

  // Convoy feasibility thresholds (ATP 4-01.45):
  // Crew duty day = 16h (includes maintenance + rest at destination)
  // >16h = overnight convoy, must stage; >48h round-trip = multi-day operation
  const sameDayFeasible = roundTripHours <= 16;

  let warning      = null;
  let warningLabel = null;
  if      (roundTripHours > 48) { warning = 'EXTREME';    warningLabel = `Extreme route (${roundTripHours.toFixed(1)}h round-trip). Plan multi-day convoy with staging area. Lead time: ${leadTimeDays} days.`; }
  else if (roundTripHours > 24) { warning = 'MULTI_DAY';  warningLabel = `Multi-day route (${roundTripHours.toFixed(1)}h round-trip). Convoy must overnight. Lead time: ${leadTimeDays} days.`; }
  else if (roundTripHours > 16) { warning = 'SLOW_ROUTE'; warningLabel = `Route exceeds crew duty day (${roundTripHours.toFixed(1)}h round-trip). Add crew rest time. Lead time: ${leadTimeDays} days.`; }

  return {
    distance_km:        distKm,
    road_type:          cfg.road_type,
    terrain:            cfg.terrain,
    season:             cfg.season,
    road_type_label:    roadSpec.label,
    terrain_label:      terrainSpec.label,
    season_label:       seasonSpec.label,
    base_speed_kmh:     baseSpeed,
    terrain_modifier:   terrainSpec.modifier,
    season_modifier:    seasonSpec.modifier,
    effective_speed_kmh: effectiveSpeed,
    one_way_hours:      oneWayHours,
    round_trip_hours:   roundTripHours,
    lead_time_days:     leadTimeDays,
    same_day_feasible:  sameDayFeasible,
    warning,
    warning_label:      warningLabel,
  };
};

/**
 * Normalize stock field names to match consumption keys.
 *
 * Tab4 stores blood products with "_units" suffix: prbc_units, ffp_units, platelets_units
 * Calculator produces consumption without suffix: prbc, ffp, platelets
 * This function maps between formats to enable calculation.
 *
 * @param {Object} stock - Initial stock from Tab4 (with _units suffix)
 * @returns {Object} Normalized stock (without _units suffix)
 *
 * @example
 * Input:  { prbc_units: 100, ffp_units: 100, iv_fluids_liters: 500 }
 * Output: { prbc: 100, ffp: 100, crystalloid: 500 }
 */
const normalizeStockKeys = (stock) => {
  if (!stock || typeof stock !== 'object') {
    throw new Error('[SupplyChain] normalizeStockKeys: Invalid stock object');
  }

  const normalized = {};

  // Field name mapping: Tab4 initial_stock keys → consumption output keys.
  // CRITICAL: All Tab4 stock field names must map to the consumption key names produced by
  // calculateDailyConsumption() — otherwise consumption subtraction silently skips those items.
  // Blood products: _units suffix stripped. Non-blood: Tab4 field name → classVIII output key.
  const fieldMap = {
    // Blood products
    'prbc_units':              'prbc',
    'ffp_units':               'ffp',
    'platelets_units':         'platelets',
    'cryoprecipitate_units':   'cryoprecipitate',
    'whole_blood_units':       'whole_blood',
    // Non-blood consumables — Tab4 field name → classVIII output key
    'iv_fluids_liters':        'crystalloid',
    'antibiotics_doses':       'antibiotics',
    'morphine_doses':          'analgesics',
    // tourniquets, chest_seals, dressings_sets already match output keys — no remapping needed
    // surgical_kits: REMOVED FROM APP (US Army ATP 4-02.25 only, not NATO doctrine)
    // controlled_substances_doses → internal key controlled_substances
    'controlled_substances_doses': 'controlled_substances',
    // oxygen_liters: no consumption rate — pass through (DOS = 9999, informational only)
  };

  Object.entries(stock).forEach(([key, value]) => {
    const normalizedKey = fieldMap[key] || key;
    normalized[normalizedKey] = value;
  });

  return normalized;
};

/**
 * Get supply category for an item.
 * @param {string} item - Supply item name
 * @returns {string} Category name or 'UNKNOWN'
 */
export const getSupplyCategory = (item) => {
  if (['prbc','ffp','platelets','whole_blood'].includes(item)) return 'BLOOD_PRODUCTS';
  if (['txa','antibiotics','analgesics','anesthetics','controlled_substances'].includes(item)) return 'PHARMACEUTICALS';
  if (['crystalloid','colloid'].includes(item)) return 'IV_FLUIDS';
  if (['tourniquets','chest_seals','bandages','airways','dressings_sets'].includes(item)) return 'CONSUMABLES';
  return 'UNKNOWN';
};

/**
 * Get wastage factor for an item.
 * @param {string} item - Supply item name
 * @returns {number} Wastage factor (0-1)
 * SCM-06: Keys updated to match classVIII.WASTAGE_FACTORS uppercase keys.
 *   Fallback changed from literal 0.10 to WASTAGE_FACTORS.DEFAULT (APP_RULES 6.1).
 */
const getWastageFactor = (item) => {
  const category = getSupplyCategory(item);
  const categoryMap = {
    'BLOOD_PRODUCTS':  WASTAGE_FACTORS.BLOOD,
    'PHARMACEUTICALS': WASTAGE_FACTORS.PHARMACEUTICALS,
    'IV_FLUIDS':       WASTAGE_FACTORS.IV_FLUIDS,
    'CONSUMABLES':     WASTAGE_FACTORS.CONSUMABLES
  };
  return categoryMap[category] ?? WASTAGE_FACTORS.DEFAULT;
};

/**
 * Calculate daily consumption based on casualty counts and national standard.
 *
 * L2-01: nationalStandard parameter added.
 * L2-02: Reads from getNationalLogistics(nationalStandard).consumption_rates instead of
 *        classVIII CONSUMPTION_RATES directly — enforces national rates (e.g. Romanian 4.5 vs NATO 6.0 PRBC/T1).
 * L2-05: tourniquets, chest_seals, analgesics (morphine) computed from per-triage nationalLogistics rates.
 *
 * @param {Object} dayCasualties - Daily casualty breakdown { t1, t2, t3, t4, dnbi }
 * @param {string} nationalStandard - National standard key (default 'NATO')
 * @returns {Object} Consumption by output key
 */
export const calculateDailyConsumption = (dayCasualties, nationalStandard = 'NATO', operationType = null) => {
  const config = getNationalLogistics(nationalStandard);
  const rates = config.consumption_rates;
  const ukraineMultiplier = config.ukraine_multipliers || {};
  const bloodMult  = ukraineMultiplier.blood_products || 1.0;
  const tqMult     = ukraineMultiplier.tourniquets    || 1.0;

  const t1   = dayCasualties?.t1   || dayCasualties?.triage_1 || 0;
  const t2   = dayCasualties?.t2   || dayCasualties?.triage_2 || 0;
  const t3   = dayCasualties?.t3   || dayCasualties?.triage_3 || 0;
  const t4   = dayCasualties?.t4   || dayCasualties?.triage_4 || 0;
  const dnbi = dayCasualties?.dnbi || 0;

  const consumption = {};

  // Blood products — per-triage with Ukraine multiplier
  consumption.prbc = Math.ceil(
    (t1 * (rates.prbc_per_t1 || 6) + t2 * (rates.prbc_per_t2 || 2) + t3 * (rates.prbc_per_t3 || 0))
    * bloodMult * (1 + getWastageFactor('prbc'))
  );
  consumption.ffp = Math.ceil(
    (t1 * (rates.ffp_per_t1 || 6) + t2 * (rates.ffp_per_t2 || 2) + t3 * (rates.ffp_per_t3 || 0))
    * bloodMult * (1 + getWastageFactor('ffp'))
  );
  consumption.platelets = Math.ceil(
    (t1 * (rates.platelets_per_t1 || 1) + t2 * (rates.platelets_per_t2 || 0))
    * bloodMult * (1 + getWastageFactor('platelets'))
  );

  // IV fluids / crystalloid — per-triage + DNBI
  consumption.crystalloid = Math.ceil(
    (t1 * (rates.iv_fluids_per_t1 || 6) + t2 * (rates.iv_fluids_per_t2 || 3) +
     t3 * (rates.iv_fluids_per_t3 || 0.5) + dnbi * (rates.iv_fluids_per_dnbi || 1))
    * (1 + getWastageFactor('crystalloid'))
  );

  // Antibiotics — per-triage + DNBI
  consumption.antibiotics = Math.ceil(
    (t1 * (rates.antibiotics_per_t1 || 3) + t2 * (rates.antibiotics_per_t2 || 2) +
     t3 * (rates.antibiotics_per_t3 || 1) + dnbi * (rates.antibiotics_per_dnbi || 5))
    * (1 + getWastageFactor('antibiotics'))
  );

  // Analgesics / morphine — per-triage from nationalLogistics (L2-05)
  // T4 expectant receives morphine only (palliation, AJMedP-1)
  consumption.analgesics = Math.ceil(
    (t1 * (rates.morphine_per_t1 || 2) + t2 * (rates.morphine_per_t2 || 1) +
     t3 * (rates.morphine_per_t3 || 0) + t4 * (rates.morphine_per_t1 || 2))
    * (1 + getWastageFactor('analgesics'))
  );

  // Tourniquets — per-triage with Ukraine multiplier (L2-05)
  consumption.tourniquets = Math.ceil(
    (t1 * (rates.tourniquet_per_t1 || 2) + t2 * (rates.tourniquet_per_t2 || 0.5) +
     t3 * (rates.tourniquet_per_t3 || 0))
    * tqMult * (1 + getWastageFactor('tourniquets'))
  );

  // Chest seals — per-triage (L2-05)
  consumption.chest_seals = Math.ceil(
    (t1 * (rates.chest_seal_per_t1 || 2) + t2 * (rates.chest_seal_per_t2 || 0.5) +
     t3 * (rates.chest_seal_per_t3 || 0))
    * (1 + getWastageFactor('chest_seals'))
  );

  // ISSUE-01 FIX + WTD-01 ENHANCEMENT: Dressings consumption.
  // When operationType provided: wound-type-weighted rates (JTS JTTR) via getWeightedDressingSets().
  // When operationType null: flat per-triage rates from nationalLogistics (ISSUE-01 baseline).
  // Zero regression: all existing call sites omit operationType → null default → unchanged behaviour.
  const dressingDNBIRate = WOUND_TYPE_DRESSING_RATE_DNBI; // 0.11 regardless of op type
  // 2026-10-11: every call site omitted operationType, so the flat rates were always used. The day's casualties now carry
  // wound_distribution (the mix after the operation type, posture, unit types and body armor); it selects the weighted path.
  const _woundDistribution = dayCasualties?.wound_distribution || null;
  if (operationType || _woundDistribution) {
    consumption.dressings_sets = Math.ceil(
      (t1   * getWeightedDressingSets(operationType, 'T1', _woundDistribution)) +
      (t2   * getWeightedDressingSets(operationType, 'T2', _woundDistribution)) +
      (t3   * getWeightedDressingSets(operationType, 'T3', _woundDistribution)) +
      (dnbi * dressingDNBIRate)
    );
  } else {
    consumption.dressings_sets = Math.ceil(
      (t1   * (rates.dressings_per_t1    || 5.0) +
       t2   * (rates.dressings_per_t2    || 3.0) +
       t3   * (rates.dressings_per_t3    || 1.0) +
       dnbi * (rates.dressings_per_dnbi  || dressingDNBIRate))
      * (1 + getWastageFactor('dressings_sets'))
    );
  }

  // Controlled substances (surgical anesthesia: ketamine TIVA + midazolam) per DCS case
  // Distinct from analgesics (battlefield pain control already in consumption.analgesics)
  // Source: military anesthesia literature; NATO: 8 doses/case, US: 12 doses/case
  // surgicalCasesPerDay: (t1 × 0.8) + (t2 × 0.3) — DCS-eligible casualties
  const surgicalCasesPerDay = (t1 * 0.8) + (t2 * 0.3);
  // surgical_kits REMOVED FROM APP — US Army ATP 4-02.25 only, not NATO doctrine.
  // Was: consumption.surgical_kits = ceil(surgicalCasesPerDay × surgical_kit_per_case × wastage)
  consumption.controlled_substances = Math.ceil(
    surgicalCasesPerDay * (rates.controlled_per_surgical || 8.0)
    * (1 + getWastageFactor('controlled_substances'))
  );

  consumption.total_casualties = t1 + t2 + t3 + t4 + dnbi;

  return consumption;
};

/**
 * Calculate average daily consumption from stock history.
 * @param {Array} dailyStock - Array of daily stock records
 * @returns {Object} Average consumption by item
 */
const calculateAverageConsumption = (dailyStock) => {
  if (!dailyStock || dailyStock.length === 0) {
    return {};
  }

  const totals = {};
  let count = 0;

  dailyStock.forEach(day => {
    if (day.consumption) {
      Object.entries(day.consumption).forEach(([item, amount]) => {
        if (item !== 'total_casualties') {
          totals[item] = (totals[item] || 0) + amount;
        }
      });
      count++;
    }
  });

  const averages = {};
  Object.entries(totals).forEach(([item, total]) => {
    averages[item] = count > 0 ? Math.ceil(total / count) : 0;
  });

  return averages;
};

/**
 * Determine request priority based on days remaining.
 * @param {number} daysRemaining - Days of supply remaining
 * @returns {Object|null} Priority info { priority, leadTime, color } or null
 */
const determineRequestPriority = (daysRemaining) => {
  if (daysRemaining <= DOS_THRESHOLDS.CRITICAL) {
    return {
      priority: 'EMERGENCY',
      leadTime: REQUEST_LEAD_TIMES.EMERGENCY,
      color: 'error'
    };
  } else if (daysRemaining <= DOS_THRESHOLDS.LOW) {
    return {
      priority: 'PRIORITY',
      leadTime: REQUEST_LEAD_TIMES.PRIORITY,
      color: 'warning'
    };
  } else if (daysRemaining <= DOS_THRESHOLDS.WARNING) {
    return {
      priority: 'ROUTINE',
      leadTime: REQUEST_LEAD_TIMES.ROUTINE,
      color: 'info'
    };
  }
  return null; // No request needed
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate supply chain status throughout operation.
 *
 * @param {Object} scenario - Full scenario object containing:
 *   - logistics.medical_supplies.initial_stock: Starting stock levels
 *   - logistics.medical_supplies.resupply_events: Planned resupply
 *   - operation_parameters.duration_days: Operation length
 * @param {Array} dailyCasualties - Daily casualty breakdown from main calculator
 *   Each element: { day, t1, t2, t3, dnbi, ... }
 * @param {string} nationalStandard - National standard key
 *
 * @returns {Object} Supply chain analysis:
 *   - daily_stock: Array of daily stock levels
 *   - days_remaining: Object with DOS per item
 *   - critical_items: Array of items below 3 DOS
 *   - average_daily_consumption: Object with avg consumption per item
 *   - g4_requests: Array of G4 logistics request recommendations
 */
/**
 * 2026-10-13 (Tab 2 plan 7.3): the supply moves on the same roads as the evacuation (MSR / ASR, bridge switch, security, insurgent, IED,
 * weather: ONE road state). Returns (event, day) => { fraction, delay_days } for ground deliveries, or null without a road effect.
 */
export const buildSupplyDeliveryModel = (scenario) => {
  const ops = scenario?.operation_parameters || {};
  const ta = ops.threat_assessment || {};
  if (!THREAT_EFFECTS_USE || !(ops.loc_config || ta.ied_threat || ta.insurgent_activity)) return null;
  const roadState = resolveRoadState({ locConfig: ops.loc_config || {}, threatAssessment: ta, weather: resolveScenarioEnvironment(scenario).weather });
  const droneLoss = DRONE_THREAT[ta.drone_threat]?.medevac_vehicle_losses ?? 0;
  return (event, day) => supplyDelivery(roadState, { day, droneWeeklyLoss: droneLoss, method: event.delivery_method });
};

export const calculateSupplyChain = (scenario, dailyCasualties, nationalStandard = 'NATO') => {
  // Comprehensive input validation (defensive programming)
  if (!scenario) {
    throw new Error('[SupplyChain] No scenario provided - cannot calculate supply chain');
  }

  if (!dailyCasualties || !Array.isArray(dailyCasualties)) {
    throw new Error('[SupplyChain] dailyCasualties must be an array - received: ' + typeof dailyCasualties);
  }

  if (dailyCasualties.length === 0) {
    throw new Error('[SupplyChain] dailyCasualties array is empty - no casualties to calculate consumption');
  }

  // Extract scenario parameters
  const rawInitialStock = scenario.logistics?.medical_supplies?.initial_stock ||
                          scenario.logistics?.medical_supplies?.detailed_items;

  if (!rawInitialStock || Object.keys(rawInitialStock).length === 0) {
    throw new Error('[SupplyChain] No initial stock found in scenario.logistics.medical_supplies.initial_stock');
  }

  // Normalize stock field names (prbc_units → prbc)
  const initialStock = normalizeStockKeys(rawInitialStock);

  const resupplyEvents = scenario.logistics?.medical_supplies?.resupply_events || [];
  const duration = scenario.operation_parameters?.duration_days;

  if (!duration || duration < 1) {
    throw new Error('[SupplyChain] Invalid operation duration: ' + duration);
  }

  // Initialize tracking arrays
  const dailyStock = [];
  let currentStock = { ...initialStock };

  const deliveryModel = buildSupplyDeliveryModel(scenario);

  // Process each day
  for (let day = 1; day <= duration; day++) {
    // Get casualties for this day (array is 0-indexed)
    const dayCasualties = dailyCasualties[day - 1] || {};

    // Calculate daily consumption based on casualty types and national standard
    const consumption = calculateDailyConsumption(dayCasualties, nationalStandard);

    // Subtract consumption from stock
    Object.keys(consumption).forEach(item => {
      if (item !== 'total_casualties' && currentStock[item] !== undefined) {
        currentStock[item] = Math.max(0, (currentStock[item] || 0) - consumption[item]);
      }
    });

    // Apply resupply if scheduled for this day (a ground delivery arrives in the share and on the day the road state allows)
    const resupply = resupplyEvents.find(e => (e.day + (deliveryModel ? deliveryModel(e, e.day).delay_days : 0)) === day);
    if (resupply?.contents) {
      const frac = deliveryModel ? deliveryModel(resupply, resupply.day).fraction : 1;
      Object.keys(resupply.contents).forEach(item => {
        currentStock[item] = (currentStock[item] || 0) + resupply.contents[item] * frac;
      });
    }

    // Record daily stock status
    dailyStock.push({
      day,
      stock: { ...currentStock },
      consumption: { ...consumption },
      resupply: resupply || null
    });
  }

  // Calculate days remaining for each category
  const lastDay = dailyStock[dailyStock.length - 1];

  if (!lastDay || !lastDay.stock) {
    throw new Error('[SupplyChain] No stock data available after calculating daily consumption');
  }

  const avgConsumption = calculateAverageConsumption(dailyStock);

  if (!avgConsumption || Object.keys(avgConsumption).length === 0) {
    throw new Error('[SupplyChain] Failed to calculate average consumption from daily stock');
  }

  const daysRemaining = {};
  Object.keys(initialStock).forEach(item => {
    const daily = avgConsumption[item] || 0;
    const remaining = lastDay.stock[item] || 0;

    if (daily === 0) {
      // Zero consumption = either not used or calculation error
      // If item has no consumption but has stock, it's unused (infinite supply)
      daysRemaining[item] = remaining > 0 ? Infinity : 0;
    } else {
      daysRemaining[item] = Math.floor(remaining / daily);
    }
  });

  // Identify critical items (< 3 days)
  const criticalItems = Object.entries(daysRemaining)
    .filter(([, days]) => days < DOS_THRESHOLDS.LOW)
    .map(([item]) => item);

  // Generate G4 requests based on consumption analysis
  const g4Requests = generateG4Requests(dailyStock, daysRemaining, duration);

  return {
    daily_stock: dailyStock,
    days_remaining: daysRemaining,
    critical_items: criticalItems,
    average_daily_consumption: avgConsumption,
    g4_requests: g4Requests,
    initial_stock: initialStock,
    total_days: duration
  };
};

/**
 * Generate G4 logistics requests with timing.
 *
 * @param {Array} dailyStock - Daily stock tracking array
 * @param {Object} daysRemaining - Days remaining per item
 * @param {number} operationDays - Total operation duration
 * @returns {Array} G4 request recommendations
 */
const generateG4Requests = (dailyStock, daysRemaining, operationDays) => {
  const requests = [];

  Object.entries(daysRemaining).forEach(([item, days]) => {
    const priorityInfo = determineRequestPriority(days);

    if (priorityInfo) {
      // Calculate request day (current day + lead time = delivery day)
      // Request needs to arrive before we run out
      const requestDay = Math.max(1, Math.ceil(operationDays - priorityInfo.leadTime - days + 1));
      const neededByDay = Math.min(operationDays, requestDay + Math.ceil(priorityInfo.leadTime));

      // Calculate quantity needed (restore to 7 DOS)
      const avgConsumption = dailyStock.reduce((sum, d) => {
        return sum + (d.consumption?.[item] || 0);
      }, 0) / Math.max(1, dailyStock.length);

      const quantityNeeded = Math.ceil(avgConsumption * (DOS_THRESHOLDS.ADEQUATE - days + 1));

      requests.push({
        item,
        request_by_day: requestDay,
        needed_by_day: neededByDay,
        current_dos: days,
        quantity_needed: quantityNeeded,
        priority: priorityInfo.priority,
        color: priorityInfo.color,
        reason: `${item.toUpperCase()} projected at ${days} DOS (below ${DOS_THRESHOLDS.ADEQUATE} threshold)`,
        category: getSupplyCategory(item)
      });
    }
  });

  // Sort by request day (earliest first)
  return requests.sort((a, b) => a.request_by_day - b.request_by_day);
};

// ============================================================================
// UTILITY FUNCTIONS FOR EXTERNAL USE
// ============================================================================

/**
 * Calculate required initial stock for target DOS.
 *
 * L2-03: nationalStandard parameter added — passed to calculateDailyConsumption() so
 *        national rates are used throughout.
 * B-01: Formula corrected. Starting stock = daily × targetDOS ONLY.
 *        operationDays parameter retained for backward-compatible signature but is NOT
 *        included in the formula. Commanded MDOS = starting stock on Day 1.
 *
 * @param {Object} estimatedDailyCasualties - Expected daily casualty rates
 * @param {number} operationDays - Operation duration (retained in signature, not used in formula)
 * @param {number} targetDOS - Target days of supply — this IS the starting stock
 * @param {string} nationalStandard - National standard key (default 'NATO')
 * @returns {Object} Required stock levels
 */
export const calculateRequiredStock = (estimatedDailyCasualties, operationDays, targetDOS, nationalStandard = 'NATO') => {
  // operationDays intentionally not used in formula (B-01 / ARCH-004).
  // Retained in signature for backward compatibility. See PSP_CRE_MASTER_FIX_PLAN_v2_0 ARCH-004.
  void operationDays;

  const dailyConsumption = calculateDailyConsumption(estimatedDailyCasualties, nationalStandard);
  const requiredStock = {};

  Object.entries(dailyConsumption).forEach(([item, dailyAmount]) => {
    if (item !== 'total_casualties') {
      // B-01: stock = commandedMDOS × daily rate (not opDuration + MDOS)
      requiredStock[item] = Math.ceil(dailyAmount * targetDOS);
    }
  });

  return requiredStock;
};

/**
 * Get supply status color for UI display.
 * @param {number} daysRemaining - Days of supply remaining
 * @returns {string} MUI color string
 */
export const getSupplyStatusColor = (daysRemaining) => {
  if (!isFinite(daysRemaining)) return 'success';
  if (daysRemaining <= DOS_THRESHOLDS.CRITICAL) return 'error';
  if (daysRemaining <= DOS_THRESHOLDS.LOW) return 'warning';
  if (daysRemaining <= DOS_THRESHOLDS.WARNING) return 'info';
  return 'success';
};

/**
 * Get supply status text for UI display.
 * @param {number} daysRemaining - Days of supply remaining
 * @returns {string} Status text
 */
export const getSupplyStatusText = (daysRemaining) => {
  if (!isFinite(daysRemaining)) return 'ADEQUATE';
  if (daysRemaining <= DOS_THRESHOLDS.CRITICAL) return 'CRITICAL';
  if (daysRemaining <= DOS_THRESHOLDS.LOW) return 'LOW';
  if (daysRemaining <= DOS_THRESHOLDS.WARNING) return 'WARNING';
  if (daysRemaining >= DOS_THRESHOLDS.ADEQUATE) return 'ADEQUATE';
  return 'NOMINAL';
};

/**
 * Format G4 request for display/export.
 * @param {Object} request - G4 request object
 * @returns {Object} Formatted request
 */
export const formatG4Request = (request) => {
  return {
    priority: request.priority,
    submit_by: `Day ${request.request_by_day}`,
    item: request.item.toUpperCase().replace(/_/g, ' '),
    quantity: request.quantity_needed,
    needed_by: `Day ${request.needed_by_day}`,
    current_dos: request.current_dos,
    reason: request.reason
  };
};

// ============================================================================
// B-02 / B-03 / B-04 / B-05: DAY-BY-DAY SIMULATION HELPER
// ============================================================================

// ============================================================================
// ENH-014c: COLD CHAIN VIABILITY HELPER
//
// computeColdChainViability(msrLegs) — given an array of MSR leg configs,
// computes the per-blood-product viability factor for arriving resupply.
//
// Model: binary per NATO AMedP-7.1 — transport limits are hard cutoffs, not
// gradients. A product whose max_hours is exceeded arrives non-viable (factor 0).
// Products with max_hours null (FDP) are always viable (factor 1).
// Products with cold_chain: false (FDP, PLT) are not affected by transit time.
//
// "Worst leg" logic: a resupply convoy must traverse ALL legs sequentially.
// The relevant travel time for viability is the leg the product is physically
// on. Without per-event leg assignment, we apply the worst single leg's travel
// time as the binding constraint for that product.
//
// @param {Array}  msrLegs - Array of leg result objects { one_way_hours }
//                           from calculateConvoyTravelTime(). Null = no degradation.
// @returns {Object} viabilityFactors — keyed by BLOOD_ITEMS item key:
//                   { prbc: 1|0, ffp: 1|0, platelets: 1|0, cryo: 1|0 }
//                   and cold_chain_meta keyed same way for diagnostics.
// ============================================================================

/**
 * Maps BLOOD_ITEMS item keys to BLOOD_TRANSPORT_LIMITS product keys.
 * BLOOD_ITEMS: ['prbc', 'ffp', 'platelets', 'cryo']
 * BLOOD_TRANSPORT_LIMITS keys: PRBC, FFP, FDP, PLT, CRYO, FWB, LTOWB
 */
const BLOOD_ITEM_TO_LIMIT_KEY = {
  prbc:      'PRBC',
  ffp:       'FFP',
  platelets: 'PLT',
  cryo:      'CRYO',
};

/**
 * ENH-014c: Compute viability factor (0 or 1) per blood item.
 * @param {Array|null} msrLegs - Array of { one_way_hours } objects
 * @returns {{ factors: Object, meta: Object }}
 *   factors: { prbc: 0|1, ffp: 0|1, platelets: 0|1, cryo: 0|1 }
 *   meta: { prbc: { limit, worst_leg_hours, viable }, ... }
 */
const computeColdChainViability = (msrLegs) => {
  const factors = {};
  const meta = {};

  // No legs configured — no degradation applied
  if (!msrLegs || !Array.isArray(msrLegs) || msrLegs.length === 0) {
    Object.keys(BLOOD_ITEM_TO_LIMIT_KEY).forEach(item => { factors[item] = 1; });
    return { factors, meta };
  }

  const worstLegHours = Math.max(...msrLegs.map(l => l.one_way_hours || 0));

  Object.entries(BLOOD_ITEM_TO_LIMIT_KEY).forEach(([item, limitKey]) => {
    const limit = BLOOD_TRANSPORT_LIMITS[limitKey];
    if (!limit) { factors[item] = 1; return; }

    // Products without cold chain requirement are unaffected by transit time
    if (!limit.cold_chain || limit.max_hours === null) {
      factors[item] = 1;
      meta[item] = { limit: null, worst_leg_hours: worstLegHours, viable: true, reason: 'No cold chain limit' };
      return;
    }

    const viable = worstLegHours <= limit.max_hours;
    factors[item] = viable ? 1 : 0;
    meta[item] = {
      limit:           limit.max_hours,
      worst_leg_hours: parseFloat(worstLegHours.toFixed(2)),
      viable,
      reason: viable
        ? `OK — ${worstLegHours.toFixed(1)}h ≤ ${limit.max_hours}h limit`
        : `NON-VIABLE — ${worstLegHours.toFixed(1)}h exceeds ${limit.max_hours}h limit`,
    };
  });

  return { factors, meta };
};

/**
 * Build day-by-day DOS simulation array.
 *
 * Used internally by calculateLogisticsProjection() to produce:
 *   daily_simulation[]  — DOS remaining per category per day  (B-02)
 *   stockout_days{}     — first day DOS reaches zero per cat  (B-04)
 *
 * Phase-aware (B-03):
 *   If perDayCasualties is an Array with per-day objects (from casualtyCalculator),
 *   each day uses its own casualty figures — DNBI-only during movement phases,
 *   full battle rates during combat phases.
 *   If perDayCasualties is a flat object, the same rate applies every day.
 *
 * Resupply spikes (B-05):
 *   resupplySchedule events add to stock on their arrival day.
 *   Supported formats per event:
 *     { day, category: 'blood'|'nato_medical_materiel', quantity }   — category total
 *     { day, item: 'prbc'|'ffp'|..., quantity }                     — specific item
 *     { day, items: { prbc: N, ffp: N, ... } }                      — multi-item
 *
 * ENH-014c (cold chain):
 *   msrLegs — array of { one_way_hours } from calculateConvoyTravelTime() for
 *   each supply route leg. Blood products arriving via a leg that exceeds their
 *   max transport time are zeroed out (factor 0). Losses recorded in cold_chain_losses[].
 *
 * @param {Array|Object} casualtiesInput  - Per-day array or flat casualty object
 * @param {string}       nationalStandard - e.g. 'NATO', 'ROMANIAN', 'US'
 * @param {number}       opDuration       - Operation duration in days
 * @param {number}       resolvedTargetMDOS - Commanded MDOS (Day 1 starting DOS)
 * @param {Array}        resupplySchedule - Resupply event list from Tab4
 * @param {Array|null}   msrLegs          - ENH-014c: leg travel times for cold chain check
 * @returns {{ dailySim: Array, stockoutDays: Object, coldChainLosses: Array }}
 */
const buildDailySimulation = (
  casualtiesInput,
  nationalStandard,
  opDuration,
  resolvedTargetMDOS,
  resupplySchedule,
  msrLegs = null,   // ENH-014c: array of { one_way_hours } per leg; null = no cold chain check
  deliveryModel = null  // 2026-10-13 (Tab 2 plan 7.3): (event, day) => { fraction, delay_days } of a ground delivery on the road state; null = no road effect
) => {
  // Build a base daily consumption object from the representative day average
  // (used as denominator to convert stock units → days-of-supply remaining)
  let repDay;
  if (Array.isArray(casualtiesInput) && casualtiesInput.length > 0) {
    const totals = { t1: 0, t2: 0, t3: 0, t4: 0, dnbi: 0 };
    casualtiesInput.forEach(d => {
      totals.t1   += d.t1   || d.triage_1 || 0;
      totals.t2   += d.t2   || d.triage_2 || 0;
      totals.t3   += d.t3   || d.triage_3 || 0;
      totals.t4   += d.t4   || d.triage_4 || 0;
      totals.dnbi += d.dnbi || 0;
    });
    const n = casualtiesInput.length;
    repDay = {
      t1: totals.t1 / n, t2: totals.t2 / n,
      t3: totals.t3 / n, t4: totals.t4 / n,
      dnbi: totals.dnbi / n
    };
  } else if (casualtiesInput && typeof casualtiesInput === 'object') {
    repDay = casualtiesInput;
  } else {
    throw new Error('[SupplyChain] buildDailySimulation: casualties input is invalid');
  }

  // Base daily consumption rates (for unit → DOS conversion)
  const baseConsumption = calculateDailyConsumption(repDay, nationalStandard);

  // Category base rates (sum of items in that category per average day)
  const bloodBase    = BLOOD_ITEMS.reduce((s, i) => s + (baseConsumption[i] || 0), 0);
  const materiелBase = MATERIEL_ITEMS.reduce((s, i) => s + (baseConsumption[i] || 0), 0);

  // Starting stock in units = daily_rate × commandedMDOS  (B-01 formula)
  const simStock = {};
  BLOOD_ITEMS.forEach(i => { simStock[i] = (baseConsumption[i] || 0) * resolvedTargetMDOS; });
  MATERIEL_ITEMS.forEach(i => { simStock[i] = (baseConsumption[i] || 0) * resolvedTargetMDOS; });

  const dailySim = [];
  const stockoutDays = { blood: null, nato_medical_materiel: null, surgical: null };

  // ENH-014c: Pre-compute cold chain viability factors once for all resupply spikes.
  // factors: { prbc: 0|1, ffp: 0|1, platelets: 0|1, cryo: 0|1 }
  const { factors: coldChainFactors, meta: coldChainMeta } = computeColdChainViability(msrLegs);
  const coldChainLosses = [];   // records of degraded/zeroed resupply units per day

  for (let d = 1; d <= opDuration; d++) {
    // Phase-aware: use per-day casualties if available (B-03)
    const dayCas = Array.isArray(casualtiesInput)
      ? (casualtiesInput[d - 1] || repDay)
      : repDay;

    const dayConsumption = calculateDailyConsumption(dayCas, nationalStandard);

    // Subtract consumption from simulation stock
    [...BLOOD_ITEMS, ...MATERIEL_ITEMS].forEach(item => {
      if (dayConsumption[item] !== undefined) {
        simStock[item] = Math.max(0, (simStock[item] || 0) - dayConsumption[item]);
      }
    });

    // Apply resupply spikes on this day (B-05) with ENH-014c cold chain viability
    const todayResupplies = resupplySchedule.filter(r => (r.day + (deliveryModel ? deliveryModel(r, r.day).delay_days : 0)) === d);
    // BUG-RESUPPLY-003: Accumulate total resupply weight for truck calculation (per-event delivery trucks).
    let resupplyWeightKgToday = 0;
    const applyResupplyEvent = (r) => {
      if (r.items && typeof r.items === 'object') {
        // Multi-item format: { day, items: { prbc: 50, ffp: 30, ... } }
        Object.entries(r.items).forEach(([item, qty]) => {
          if (simStock[item] !== undefined) {
            const factor = (coldChainFactors[item] !== undefined) ? coldChainFactors[item] : 1;
            const effective = Math.round(qty * factor);
            if (factor < 1) coldChainLosses.push({ day: d, item, ordered: qty, applied: effective, factor, meta: coldChainMeta[item] });
            simStock[item] = (simStock[item] || 0) + effective;
            resupplyWeightKgToday += effective * (ITEM_WEIGHTS_KG[item] || 0); // BUG-RESUPPLY-003
          }
        });
      } else if (r.item && simStock[r.item] !== undefined) {
        // Single-item format: { day, item: 'prbc', quantity: 50 }
        const item = r.item;
        const qty = r.quantity || 0;
        const factor = (coldChainFactors[item] !== undefined) ? coldChainFactors[item] : 1;
        const effective = Math.round(qty * factor);
        if (factor < 1) coldChainLosses.push({ day: d, item, ordered: qty, applied: effective, factor, meta: coldChainMeta[item] });
        simStock[item] = (simStock[item] || 0) + effective;
        resupplyWeightKgToday += effective * (ITEM_WEIGHTS_KG[item] || 0); // BUG-RESUPPLY-003
      } else if (r.push_package) {
        // Format 4: Tab4 event with push_package — three sub-cases:
        //   4a. Manual event WITH explicit quantity_modifier: use modifier directly.
        //   4b. Manual event WITHOUT quantity_modifier (BUG-RESUPPLY-001): derive force-scaled
        //       modifier from bloodBase / STANDARD_RESUPPLY_BASE_PRBC so a manual event
        //       delivers the same quantity as an auto-scheduled event for this force size.
        //   4c. Auto-generated event: { push_package, supplies: {}, quantity_modifier } — modifier set.
        //       supplies.blood_products absent → _unitsFromModifier(). PKG-003 path unchanged.
        const modifier = r.quantity_modifier != null
          ? r.quantity_modifier
          : Math.max(1, Math.round(bloodBase / STANDARD_RESUPPLY_BASE_PRBC)); // BUG-RESUPPLY-001 fix
        const bp = r.supplies?.blood_products;
        // BUG-TAB4-003 FIX: check bp != null (key exists), not bp && (values).
        // When a package like TCCC_REPLENISHMENT has blood_products:{prbc_units:0,...},
        // all values are falsy → old check fell through to _unitsFromModifier() and
        // delivered phantom 50+ PRBC. Now: key present = use explicit values (even if 0).
        // _unitsFromModifier() is only for auto-generated events with supplies:{} (no key).
        const hasBP = bp != null;
        const units = hasBP
          ? {
              prbc:      Math.round((bp.prbc_units      || 0) * modifier),
              ffp:       Math.round((bp.ffp_units        || 0) * modifier),
              platelets: Math.round((bp.platelets_units  || 0) * modifier),
              cryo:      Math.round((bp.cryo_units       || 0) * modifier),
            }
          : _unitsFromModifier(modifier);  // PKG-003 fix: auto events derive units from modifier
        // PKG-003b: MASS_CASUALTY_PUSH carries whole_blood_collection_kits (WBB) not platelets.
        const wbbKits = bp?.whole_blood_collection_kits || 0;
        if (wbbKits > 0) units.prbc = (units.prbc || 0) + Math.round(wbbKits * 2);
        Object.entries(units).forEach(([item, qty]) => {
          if (qty > 0 && simStock[item] !== undefined) {
            const factor = (coldChainFactors[item] !== undefined) ? coldChainFactors[item] : 1;
            const effective = Math.round(qty * factor);
            if (factor < 1) coldChainLosses.push({ day: d, item, ordered: qty, applied: effective, factor, meta: coldChainMeta[item] });
            simStock[item] = (simStock[item] || 0) + effective;
            resupplyWeightKgToday += effective * (ITEM_WEIGHTS_KG[item] || 0);
          }
        });
        // BUG-RESUPPLY-004 / BUG-TAB4-004: Apply materiel items from r.supplies.
        // Tab4 stores the nested PUSH_PACKAGES[...].contents structure:
        //   { blood_products:{...}, pharmaceuticals:{iv_fluids_liters:0,...},
        //     consumables:{tourniquets:100, chest_seals:50,...} }
        // The original flat-key iteration missed nested sub-objects entirely.
        // Fix: skip blood_products (handled above), then recurse into any sub-object
        // (pharmaceuticals, consumables, etc.) applying the field map to leaf keys.
        if (r.supplies && typeof r.supplies === 'object') {
          const materielFieldMap = {
            'iv_fluids_liters':            'crystalloid',
            'antibiotics_doses':           'antibiotics',
            'morphine_doses':              'analgesics',
            'controlled_substances_doses': 'controlled_substances',
            'tourniquets':                 'tourniquets',
            'chest_seals':                 'chest_seals',
            'dressings_sets':              'dressings_sets',
          };
          // Helper: credit a single key/value pair to simStock
          const creditItem = (rawKey, rawQty) => {
            if (rawKey === 'blood_products') return;
            const simKey = materielFieldMap[rawKey] || rawKey;
            if (simStock[simKey] !== undefined && rawQty > 0) {
              const qty = Math.round(rawQty * modifier);
              simStock[simKey] = (simStock[simKey] || 0) + qty;
              resupplyWeightKgToday += qty * (ITEM_WEIGHTS_KG[simKey] || 0);
            }
          };
          Object.entries(r.supplies).forEach(([rawKey, rawVal]) => {
            if (rawKey === 'blood_products') return; // already handled above
            if (rawVal && typeof rawVal === 'object' && !Array.isArray(rawVal)) {
              // Nested sub-object (pharmaceuticals, consumables, etc.) — recurse one level
              Object.entries(rawVal).forEach(([subKey, subQty]) => creditItem(subKey, subQty));
            } else {
              // Flat key (custom editor path)
              creditItem(rawKey, rawVal);
            }
          });
        }
      } else if (r.category) {
        // Category-level format: { day, category: 'blood', quantity: 100 }
        // Distribute proportionally to category items using base consumption weights
        const cat = String(r.category).toLowerCase();
        if (cat === 'blood' || cat === 'blood_products') {
          const totalBase = bloodBase > 0 ? bloodBase : 1;
          BLOOD_ITEMS.forEach(item => {
            const share = (baseConsumption[item] || 0) / totalBase;
            const qty = (r.quantity || 0) * share;
            const factor = (coldChainFactors[item] !== undefined) ? coldChainFactors[item] : 1;
            const effective = qty * factor;
            if (factor < 1) coldChainLosses.push({ day: d, item, ordered: parseFloat(qty.toFixed(1)), applied: parseFloat(effective.toFixed(1)), factor, meta: coldChainMeta[item] });
            simStock[item] = (simStock[item] || 0) + effective;
            resupplyWeightKgToday += effective * (ITEM_WEIGHTS_KG[item] || 0); // BUG-RESUPPLY-003
          });
        } else if (cat === 'nato_medical_materiel' || cat === 'class_viii' || cat === 'materiel') {
          const totalBase = materiелBase > 0 ? materiелBase : 1;
          MATERIEL_ITEMS.forEach(item => {
            const share = (baseConsumption[item] || 0) / totalBase;
            const qty = (r.quantity || 0) * share;
            simStock[item] = (simStock[item] || 0) + qty;
            resupplyWeightKgToday += qty * (ITEM_WEIGHTS_KG[item] || 0); // BUG-RESUPPLY-003
          });
        }
      } else if (r.delivery_method === 'DRONE') {
        // ENH-008: Drone UAS medical supply delivery.
        // Source: Ukraine 2022-2024 / US Army Project Convergence 22.
        // Drone events carry blood products and/or TCCC consumables forward of R2B.
        // Payload is limited by drone spec. AD threat degrades availability.
        // Cold chain viability is checked but not zeroed — planner is responsible.
        const droneType = r.drone_type || 'CARGO_DRONE';
        const spec = DRONE_DELIVERY_SPECS[droneType] ?? DRONE_DELIVERY_SPECS.CARGO_DRONE;

        // Resolve AD threat from scenario (optional — if not present, use MEDIUM as default)
        const adThreat = (r.ad_threat_override || 'MEDIUM').toUpperCase();
        const availability = spec.ad_threat_availability[adThreat]
                          ?? spec.ad_threat_availability.MEDIUM
                          ?? 0.75;

        // Blood products — only for drones that are cold-chain capable
        if (spec.cold_chain_viable && spec.blood_units_per_flight > 0) {
          const bloodQty = Math.round(spec.blood_units_per_flight * availability);
          if (bloodQty > 0 && simStock.prbc !== undefined) {
            // Cold chain: viable per Lammers 2023 Transfusion if flight < 2h and temp < 25°C.
            // We apply a conservative 0.95 factor for in-flight temperature variance
            const coldFactor = 0.95;
            const effectiveBlood = Math.round(bloodQty * coldFactor);
            simStock.prbc = (simStock.prbc || 0) + effectiveBlood;
            resupplyWeightKgToday += effectiveBlood * (requireNumber(ITEM_WEIGHTS_KG.prbc, 'supplyChainModule.js:1349', 'ITEM_WEIGHTS_KG.prbc'));
            if (coldFactor < 1) {
              coldChainLosses.push({
                day: d, item: 'prbc', ordered: bloodQty, applied: effectiveBlood,
                factor: coldFactor, meta: { source: 'drone_flight_variance', spec: spec.cold_chain_note }
              });
            }
          }
        }

        // TCCC consumables — tourniquets always deliverable regardless of cold chain
        if (spec.tourniquet_packs > 0) {
          const tourniqs = Math.round(spec.tourniquet_packs * availability);
          if (tourniqs > 0 && simStock.tourniquets !== undefined) {
            simStock.tourniquets = (simStock.tourniquets || 0) + tourniqs;
            resupplyWeightKgToday += tourniqs * (requireNumber(ITEM_WEIGHTS_KG.tourniquets, 'supplyChainModule.js:1361', 'ITEM_WEIGHTS_KG.tourniquets'));
          }
        }
      }
    };
    todayResupplies.forEach(r => {
      // 2026-10-13 (Tab 2 plan 7.3): ground deliveries arrive in the share the road state allows (availability, drone supply vehicle loss)
      const frac = deliveryModel ? deliveryModel(r, r.day).fraction : 1;
      const before = { ...simStock };
      const weightBefore = resupplyWeightKgToday;
      applyResupplyEvent(r);
      if (frac < 1) {
        Object.keys(simStock).forEach(item => { simStock[item] = (before[item] || 0) + ((simStock[item] || 0) - (before[item] || 0)) * frac; });
        resupplyWeightKgToday = weightBefore + (resupplyWeightKgToday - weightBefore) * frac;
      }
    });

    // Compute category DOS remaining (units / base_daily_rate)
    const bloodStock    = BLOOD_ITEMS.reduce((s, i) => s + (simStock[i] || 0), 0);
    const materiелStock = MATERIEL_ITEMS.reduce((s, i) => s + (simStock[i] || 0), 0);

    const bloodDOS    = bloodBase    > 0 ? bloodStock    / bloodBase    : resolvedTargetMDOS;
    const materiелDOS = materiелBase > 0 ? materiелStock / materiелBase : resolvedTargetMDOS;

    // Stockout detection — record FIRST day DOS reaches zero (B-04)
    if (bloodDOS <= 0 && stockoutDays.blood === null)                     stockoutDays.blood = d;
    if (materiелDOS <= 0 && stockoutDays.nato_medical_materiel === null) stockoutDays.nato_medical_materiel = d;
    // surgical: placeholder — Tab5 computes from surgical team data

    dailySim.push({
      day: d,
      blood_remaining:                 parseFloat(Math.max(0, bloodDOS).toFixed(1)),
      nato_medical_materiel_remaining: parseFloat(Math.max(0, materiелDOS).toFixed(1)),
      surgical_remaining:              null,  // placeholder — computed by Tab5
      // BUG-RESUPPLY-003: trucks needed to deliver today's resupply (0 on non-resupply days).
      // Replaces trucks_required (consumption trucks) for the Tab5 Trucks column.
      trucks_resupply: resupplyWeightKgToday > 0
        ? Math.ceil(resupplyWeightKgToday / (requireNumber(CONVOY_CAPACITY.STANDARD_TRUCK?.effective_payload_kg, 'supplyChainModule.js:1387', 'CONVOY_CAPACITY.STANDARD_TRUCK.effective_payload_kg')))
        : 0,
      // ENH-010-C: Daily weight/volume consumed that day (not cumulative)
      ...(() => {
        const wv = calculateDailyWeightVolume(dayConsumption);
        return {
          blood_weight_kg:    wv.blood_weight_kg,
          blood_volume_m3:    wv.blood_volume_m3,
          materiel_weight_kg: wv.materiel_weight_kg,
          materiel_volume_m3: wv.materiel_volume_m3,
          total_weight_kg:    wv.total_weight_kg,
          total_volume_m3:    wv.total_volume_m3,
          trucks_required:    wv.trucks_required,  // consumption trucks — retained for convoy planning
        };
      })(),
    });
  }

  return { dailySim, stockoutDays, coldChainLosses };
};

// ============================================================================
// v4.3.0: CALCULATOR OUTPUT EXTRACTOR
// casualtyCalculator.js produces a rich object (by_corps, by_brigade, by_day).
// calculateLogisticsProjection() must handle 4 distinct input shapes:
//   Shape A — Array of per-day objects { t1, t2, t3, t4, dnbi }
//   Shape B — Flat object with t1/t2/t3/dnbi fields directly
//   Shape C — Object with daily_casualties sub-object (legacy)
//   Shape D — Full casualtyCalculator output { by_corps, by_brigade, ... }
// The previous code fell through to `repDay = casualties` for Shape D, assigning
// the entire calculator object as the casualty descriptor. t1/t2/t3/dnbi were all
// undefined → 0 → consumption = 0 → required_stock = 0 → CRITICAL_ZERO display.
// ============================================================================

/**
 * Extract a representative daily casualty object { t1, t2, t3, t4, dnbi }
 * and optionally a per-day array from any casualtyCalculator output shape.
 *
 * @param {*} casualties - Any form of casualty data passed to calculateLogisticsProjection
 * @returns {{ repDay: Object, perDayArray: Array|null }}
 * @throws {Error} If no usable casualty data can be extracted
 */
const extractFromCalculatorOutput = (casualties) => {
  // Shape A — Array of per-day objects (phase-aware simulation path)
  if (Array.isArray(casualties)) {
    if (casualties.length === 0) {
      throw new Error('[SupplyChain] extractFromCalculatorOutput: casualties array is empty');
    }
    const totals = { t1: 0, t2: 0, t3: 0, t4: 0, dnbi: 0 };
    casualties.forEach(d => {
      totals.t1   += d.t1   || d.triage_1 || 0;
      totals.t2   += d.t2   || d.triage_2 || 0;
      totals.t3   += d.t3   || d.triage_3 || 0;
      totals.t4   += d.t4   || d.triage_4 || 0;
      totals.dnbi += d.dnbi || 0;
    });
    const n = casualties.length;
    const repDay = {
      t1: totals.t1 / n, t2: totals.t2 / n,
      t3: totals.t3 / n, t4: totals.t4 / n,
      dnbi: totals.dnbi / n
    };
    return { repDay, perDayArray: casualties };
  }

  if (!casualties || typeof casualties !== 'object') {
    throw new Error('[SupplyChain] extractFromCalculatorOutput: casualties must be an array or object');
  }

  // Shape D — Full casualtyCalculator output: { by_corps, by_brigade, ... }
  // This is the most common shape when Tab4 reads scenario.casualty_projections
  // (the full calculator result saved to scenario after Tab5 runs).
  // Also handles the preliminary result from Tab4's own calculateCasualties() call,
  // which uses the same by_corps structure.
  if (casualties.by_corps && typeof casualties.by_corps === 'object') {
    const corps = casualties.by_corps;

    // Triage breakdown: calculator stores total_wia_t1/t2/t3 on by_corps.
    // If per-triage fields absent, fall back to standard distribution.
    const totalWIA  = corps.total_wia  || 0;
    const totalDNBI = corps.total_dnbi || 0;
    const opDays    = casualties.operation_parameters?.duration_days || 1;

    // Per-triage WIA (cumulative totals → daily average)
    const t1Total = corps.total_wia_t1 || Math.round(totalWIA * 0.15);
    const t2Total = corps.total_wia_t2 || Math.round(totalWIA * 0.25);
    const t3Total = corps.total_wia_t3 || Math.round(totalWIA * 0.55);
    const t4Total = corps.total_kia    || 0;   // T4/expectant ≈ KIA for consumption (palliation)

    const repDay = {
      t1:   t1Total / opDays,
      t2:   t2Total / opDays,
      t3:   t3Total / opDays,
      t4:   t4Total / opDays,
      dnbi: totalDNBI / opDays,
    };

    // Build per-day array from by_brigade.by_day for phase-aware simulation
    let perDayArray = null;
    if (Array.isArray(casualties.by_brigade) && casualties.by_brigade.length > 0) {
      const firstBrigadeWithDays = casualties.by_brigade.find(b => Array.isArray(b.by_day) && b.by_day.length > 0);
      if (firstBrigadeWithDays) {
        const dayCount = firstBrigadeWithDays.by_day.length;
        const dayArrays = new Array(dayCount).fill(null).map((_, idx) => ({
          t1: 0, t2: 0, t3: 0, t4: 0, dnbi: 0
        }));

        for (const brigade of casualties.by_brigade) {
          if (!Array.isArray(brigade.by_day)) continue;
          for (const dayData of brigade.by_day) {
            const dayIdx = (dayData.day || 1) - 1;
            if (dayIdx < 0 || dayIdx >= dayCount) continue;
            // battle.wia → split by triage distribution; dnbi.total or flat dnbi
            const dayWIA  = dayData.battle?.wia || dayData.wia  || 0;
            const dayKIA  = dayData.battle?.kia || dayData.kia  || 0;
            const dayDNBI = dayData.dnbi?.total || dayData.dnbi || 0;
            dayArrays[dayIdx].t1   += Math.round(dayWIA * 0.15);
            dayArrays[dayIdx].t2   += Math.round(dayWIA * 0.25);
            dayArrays[dayIdx].t3   += Math.round(dayWIA * 0.55);
            dayArrays[dayIdx].t4   += Math.round(dayKIA * 0.05); // expectant fraction
            dayArrays[dayIdx].dnbi += dayDNBI;
          }
        }
        perDayArray = dayArrays;
      }
    }

    // F3-MRoE-STREAM2: Add civilian Stream 2 patients to daily blood/supply demand.
    // Doctrine: civilian T1 (surgical/immediate) and T2 (ward/delayed) patients enter the
    // main military chain at R2B — they consume blood and surgical capacity.
    // EPW stream (Stream 1) is EXCLUDED — it flows through a dedicated EPW section,
    // not the main R1→R2→R3 chain (AJMedP-1 §3.21, GC III Art.12).
    // T3 civilian patients go to HNS hospitals → also excluded.
    // Only applied when civilian_stream.competes_with_military === true (matrix-driven).
    const _civStream = casualties.mascal_analysis?.mroe_load?.civilian_stream;
    if (_civStream && _civStream.competes_with_military) {
      const _civT1 = _civStream.t1_surgical_daily || 0;
      const _civT2 = _civStream.t2_ward_daily     || 0;
      // Add to representative daily averages
      repDay.t1 += _civT1;
      repDay.t2 += _civT2;
      // Distribute evenly across per-day array (V3.1: flat daily addition;
      // V4: phase-linked civilian load per operational phase)
      if (perDayArray) {
        perDayArray.forEach(d => { d.t1 += _civT1; d.t2 += _civT2; });
      }
    }

    return { repDay, perDayArray };
  }

  // Shape B — Flat object with t1/t2/t3/dnbi directly (e.g. Tab4 fallbackCasualtyProjection)
  if (casualties.t1 !== undefined || casualties.triage_1 !== undefined || casualties.dnbi !== undefined) {
    const repDay = {
      t1:   casualties.t1   || casualties.triage_1 || 0,
      t2:   casualties.t2   || casualties.triage_2 || 0,
      t3:   casualties.t3   || casualties.triage_3 || 0,
      t4:   casualties.t4   || casualties.triage_4 || 0,
      dnbi: casualties.dnbi || 0,
    };
    return { repDay, perDayArray: null };
  }

  // Shape B (summary variant) — { total, by_triage: { t1, t2, t3, t4 }, daily_average }
  // Produced by Tab4's fallbackCasualtyProjection and preliminaryCasualties summary
  if (casualties.by_triage && typeof casualties.by_triage === 'object') {
    const opDays = casualties.duration_days || 1;
    const bt = casualties.by_triage;
    const repDay = {
      t1:   (bt.t1 || bt.T1 || 0) / opDays,
      t2:   (bt.t2 || bt.T2 || 0) / opDays,
      t3:   (bt.t3 || bt.T3 || 0) / opDays,
      t4:   (bt.t4 || bt.T4 || 0) / opDays,
      dnbi: (casualties.total_dnbi || 0) / opDays,
    };
    return { repDay, perDayArray: null };
  }

  // Shape C — Legacy: object with daily_casualties sub-object
  if (casualties.daily_casualties && typeof casualties.daily_casualties === 'object') {
    return { repDay: casualties.daily_casualties, perDayArray: null };
  }

  // Shape D fallback — summary fields on root (total_wia, total_dnbi, duration_days)
  if (casualties.total_wia !== undefined || casualties.total_dnbi !== undefined) {
    const opDays = casualties.duration_days || 1;
    const totalWIA  = casualties.total_wia  || 0;
    const totalDNBI = casualties.total_dnbi || 0;
    const repDay = {
      t1:   Math.round(totalWIA * 0.15) / opDays,
      t2:   Math.round(totalWIA * 0.25) / opDays,
      t3:   Math.round(totalWIA * 0.55) / opDays,
      t4:   Math.round(totalWIA * 0.05) / opDays,
      dnbi: totalDNBI / opDays,
    };
    return { repDay, perDayArray: null };
  }

  throw new Error(
    '[SupplyChain] extractFromCalculatorOutput: Cannot extract casualty data. ' +
    'Expected Array, by_corps object, by_triage object, or flat t1/t2/t3 object. ' +
    'Received keys: ' + Object.keys(casualties).slice(0, 8).join(', ')
  );
};

// ============================================================================
// B-06 / B-07: LOGISTICS PROJECTION (Tab4 trigger → localStorage)
// ============================================================================

/**
 * Calculate complete logistics projection for the operation.
 *
 * OWNERSHIP: This function is called by Tab4's Save & Continue (ARCH-001 / ARCH-009).
 * Tab3 must NOT call this function. Tab3 calls casualtyCalculator only.
 *
 * B-06: Signature updated — resupplySchedule parameter added.
 * B-01: Stock formula corrected — starting stock = daily × targetMDOS (not daily × (opDuration + targetMDOS)).
 * B-07: Output shape extended — daily_simulation and stockout_days added.
 *
 * @param {Object|Array} casualties       - Casualty calculator output (array = phase-aware, object = flat)
 * @param {string}       nationalStandard - From Tab1 saved data — throws if missing (APP_RULES 6.1)
 * @param {number}       opDuration       - Operation duration in days — from Tab2 saved data
 * @param {number|null}  targetMDOS       - Commanded MDOS (null = use national TOTAL from nationalLogistics)
 * @param {Array}        resupplySchedule - Resupply events from Tab4 (default empty)
 * @returns {Object} Complete logistics projection saved to localStorage 'psp_logistics_projection'
 *
 * Output shape:
 * {
 *   national_standard, op_duration_days, target_mdos_days,
 *   commanded_mdos_days,             // null — Tab4 user sets this separately
 *   daily_consumption,               // average daily consumption per item
 *   required_stock,                  // required starting stock per item (= daily × targetMDOS)
 *   current_stock,                   // empty — Tab4 populates from user input
 *   days_remaining,                  // days-of-supply at start (= targetMDOS for all items)
 *   limiting_factor, overall_mdos, status,
 *   by_category: { blood, nato_medical_materiel, surgical },
 *   stockout_days: { blood, nato_medical_materiel, surgical },  // B-04: null = no stockout
 *   daily_simulation: [                                         // B-02: day-by-day DOS remaining
 *     { day, blood_remaining, nato_medical_materiel_remaining, surgical_remaining },
 *     ...
 *   ],
 *   calculated_at
 * }
 */
export const calculateLogisticsProjection = (
  casualties,
  nationalStandard,
  opDuration,
  targetMDOS = null,
  resupplySchedule = [],
  msrConfig = null,       // ARCH-002: optional MSR Leg 1 config for convoy_plan
  msrLegs = null,         // ENH-014c: array of { one_way_hours } for cold chain viability check
  deliveryModel = null    // 2026-10-13 (Tab 2 plan 7.3): (event, day) => { fraction, delay_days } on the road state (see buildSupplyDeliveryModel); null = no road effect
) => {
  // APP_RULES 6.1 — throw on missing inputs, no silent fallbacks
  if (!nationalStandard) {
    throw new Error('[SupplyChain] calculateLogisticsProjection: nationalStandard is required — pass from Tab1 saved data');
  }
  if (!opDuration || opDuration < 1) {
    throw new Error('[SupplyChain] calculateLogisticsProjection: opDuration must be >= 1 — pass from Tab2 saved data');
  }
  if (!casualties) {
    throw new Error('[SupplyChain] calculateLogisticsProjection: casualties output from casualtyCalculator is required');
  }

  const config = getNationalLogistics(nationalStandard);

  // Default targetMDOS to national TOTAL MDOS if not specified
  const resolvedTargetMDOS = targetMDOS ?? config.mdos_lines?.TOTAL?.days ?? 20;

  // v4.3.0: Use extractFromCalculatorOutput() to handle all 4 input shapes correctly.
  // Previously: fell through to `repDay = casualties` for Shape D (full calculator output),
  // assigning the entire calculator object as the daily descriptor → t1/t2/t3 all 0 → consumption 0.
  const { repDay, perDayArray } = extractFromCalculatorOutput(casualties);
  const casualtiesArray = perDayArray;

  // Calculate average daily consumption at national rates
  const rawDaily = calculateDailyConsumption(repDay, nationalStandard);

  // B-01: Build daily_consumption and required_stock using CORRECTED formula.
  // requiredStock = daily × targetMDOS  (Commanded MDOS = Day 1 starting stock)
  // daysRemaining at start = resolvedTargetMDOS for every item (trivially correct on Day 1)
  const dailyConsumption = {};
  const requiredStock = {};
  const daysRemaining = {};

  Object.entries(rawDaily).forEach(([item, dailyAmount]) => {
    if (item === 'total_casualties') return;
    dailyConsumption[item] = dailyAmount;
    // B-01: formula corrected — daily × targetMDOS only
    requiredStock[item] = Math.ceil(dailyAmount * resolvedTargetMDOS);
    // Starting DOS = resolvedTargetMDOS (simulation shows how this degrades day by day)
    daysRemaining[item] = resolvedTargetMDOS;
  });

  // Group into categories for Tab5 display
  const categoryDOS = (items) => {
    const vals = items.map(i => daysRemaining[i]).filter(v => v !== undefined && isFinite(v));
    return vals.length > 0 ? Math.min(...vals) : resolvedTargetMDOS;
  };

  const byCategory = {
    blood: {
      items: BLOOD_ITEMS,
      days_remaining:    categoryDOS(BLOOD_ITEMS),
      daily_consumption: Object.fromEntries(BLOOD_ITEMS.map(i => [i, dailyConsumption[i] || 0])),
      required_stock:    Object.fromEntries(BLOOD_ITEMS.map(i => [i, requiredStock[i]    || 0])),
    },
    // C-001: internal key 'class_viii' retained (APP_RULES — internal names unchanged)
    // UI label must read "NATO Medical Materiel" — change in Tab4/Tab5 display strings only
    nato_medical_materiel: {
      items: MATERIEL_ITEMS,
      days_remaining:    categoryDOS(MATERIEL_ITEMS),
      daily_consumption: Object.fromEntries(MATERIEL_ITEMS.map(i => [i, dailyConsumption[i] || 0])),
      required_stock:    Object.fromEntries(MATERIEL_ITEMS.map(i => [i, requiredStock[i]    || 0])),
    },
    surgical: {
      // Surgical DOS computed from team counts in Tab3/Tab5 — not from supply consumption rates.
      // Placeholder populated by Tab5 using surgical_config from scenario.
      items: [],
      days_remaining: null,
      daily_consumption: {},
      required_stock: {},
    },
  };

  // Overall MDOS = starting DOS = resolvedTargetMDOS (all items start at commanded level)
  // The day-by-day simulation reveals the true depletion curve and stockout days.
  const overallMDOS = resolvedTargetMDOS;
  const limitingEntry = ['commanded_mdos', resolvedTargetMDOS];

  // Status against national DOS thresholds
  const thresholds = config.dos_thresholds;
  let status = 'ADEQUATE';
  if      (overallMDOS <= thresholds.critical) status = 'CRITICAL';
  else if (overallMDOS <= thresholds.minimum)  status = 'MINIMUM';
  else if (overallMDOS >= thresholds.optimal)  status = 'OPTIMAL';

  // B-02 / B-03 / B-04 / B-05: Build day-by-day simulation
  const simulationInput = casualtiesArray || repDay;
  const { dailySim, stockoutDays, coldChainLosses } = buildDailySimulation(
    simulationInput,
    nationalStandard,
    opDuration,
    resolvedTargetMDOS,
    resupplySchedule,
    msrLegs,   // ENH-014c: cold chain viability check per leg
    deliveryModel
  );

  return {
    national_standard:    nationalStandard,
    op_duration_days:     opDuration,
    target_mdos_days:     resolvedTargetMDOS,
    commanded_mdos_days:  null,            // set by Tab4 user input
    daily_consumption:    dailyConsumption,
    required_stock:       requiredStock,
    current_stock:        {},              // populated by Tab4 user input
    days_remaining:       daysRemaining,
    limiting_factor:      limitingEntry[0],
    overall_mdos:         overallMDOS,
    status,
    by_category:          byCategory,
    // B-04: null = no stockout within operation duration
    stockout_days:        stockoutDays,
    // B-02: day-by-day DOS remaining for Tab5 timeline graph
    daily_simulation:     dailySim,
    // BUG-RESUPPLY-002: resupply_events forwarded so Tab5 can annotate timeline rows.
    // Previously absent — Tab5 read logisticsProjection.resupply_events || [] → always [].
    resupply_events:      resupplySchedule,
    // ENH-010-D: average daily weight/volume (from mean casualty rate, not per-day)
    daily_weight_volume:  calculateDailyWeightVolume(dailyConsumption),
    // ARCH-002: convoy travel time plan (null if no MSR config provided)
    convoy_plan:          msrConfig ? calculateConvoyTravelTime(msrConfig) : null,
    // ENH-014c: cold chain degradation results
    // cold_chain_losses: [] when no legs configured or no blood products degraded
    // cold_chain_viability: per-product viability summary from computeColdChainViability()
    cold_chain_losses:    coldChainLosses,
    cold_chain_viability: (() => {
      const { factors, meta } = computeColdChainViability(msrLegs);
      const anyNonViable = Object.values(factors).some(f => f < 1);
      return { factors, meta, any_non_viable: anyNonViable };
    })(),
    calculated_at:        new Date().toISOString(),
  };
};



// ============================================================================
// RESUPPLY SCHEDULER — v4.1.0 (February 28, 2026)
// buildDailySimulation — v4.2.0: Format 4 (push_package + supplies.blood_products) for manual Tab4 events
//
// CRIT-1 FIX: Previously imported from non-existent ResupplySchedulerService.
// Consolidated here. Tab4 import changes to:
//   import { generateSchedule, STRATEGIES } from '../utils/supplyChainModule'
//
// Static PUSH_PACKAGE counts (50 PRBC/event) are replaced with force-scaled
// quantities: quantity_modifier = ceil(neededPRBC / 50).
// calculateResupplyUnits(push_package, modifier) then scales all blood products.
// ============================================================================

/** Base PRBC units in STANDARD_RESUPPLY (classVIII.js). */
const STANDARD_RESUPPLY_BASE_PRBC = 50;

/**
 * PKG-002 FIX: Strategy definitions now use labels/descriptions only.
 * targetDOS is resolved at runtime from THRESHOLD_PROFILES[nationalStandard]:
 *   CONSERVATIVE → caution_threshold  (maintain this DOS throughout)
 *   BALANCED     → warning_threshold  (trigger resupply at warning level)
 *   MINIMAL      → critical_threshold (trigger only at critical level)
 * Previous hardcoded values (7/5/3) were NATO-only and ignored national standards.
 */
export const STRATEGIES = {
  CONSERVATIVE: {
    label: 'Conservative',
    description: 'Maintain national target DOS throughout (NATO:15d, Romanian:18d, US:12d)',
    // PKG-002: resolved via THRESHOLD_PROFILES[natStandard].resupply.target_dos
    thresholdPath: 'resupply.target_dos',
    safetyMargin: 2,
    minEventGap: 2
  },
  BALANCED: {
    label: 'Balanced',
    description: 'Maintain routine resupply threshold (NATO:7d, Romanian:8d, US:6d)',
    // PKG-002: resolved via THRESHOLD_PROFILES[natStandard].resupply.routine_threshold
    thresholdPath: 'resupply.routine_threshold',
    safetyMargin: 1,
    minEventGap: 3
  },
  MINIMAL: {
    label: 'Minimal',
    description: 'Avoid stockout only — trigger at emergency threshold (NATO:3d, Romanian:4d, US:2d)',
    // PKG-002: resolved via THRESHOLD_PROFILES[natStandard].resupply.emergency_threshold
    thresholdPath: 'resupply.emergency_threshold',
    safetyMargin: 1,
    minEventGap: 4
  }
};

/**
 * Resolve effective targetDOS for a strategy + national standard.
 * Falls back to STRATEGIES legacy targetDOS (7/5/3) if THRESHOLD_PROFILES unavailable.
 * @param {Object} strategy     - Entry from STRATEGIES
 * @param {string} natStandard  - 'NATO' | 'ROMANIAN' | 'US'
 * @returns {number} targetDOS in days
 */
const _resolveTargetDOS = (strategy, natStandard) => {
  try {
    const profile = getThresholdProfile(natStandard);
    if (profile && strategy.thresholdPath) {
      // thresholdPath is 'resupply.target_dos' / 'resupply.routine_threshold' / etc.
      const [section, field] = strategy.thresholdPath.split('.');
      const value = profile[section]?.[field];
      if (typeof value === 'number' && value > 0) return value;
    }
  } catch (_) { /* fall through */ }
  // Graceful fallback to pre-PKG-002 hardcoded values (covers missing profile)
  const legacy = { CONSERVATIVE: 15, BALANCED: 7, MINIMAL: 3 };
  return legacy[Object.keys(STRATEGIES).find(k => STRATEGIES[k] === strategy)] || 7;
};

/** Units delivered for a given modifier (mirrors calculateResupplyUnits). */
const _unitsFromModifier = (modifier) => ({
  prbc: modifier * STANDARD_RESUPPLY_BASE_PRBC,
  ffp:  modifier * 40,
  plt:  modifier * 20,
  cryo: modifier * 5
});

/** Choose delivery method based on urgency. */
const _selectDeliveryMethod = (daysUntilStockout, safetyMargin) => {
  if (daysUntilStockout <= safetyMargin)     return 'BLUE_LIGHT';
  if (daysUntilStockout <= safetyMargin + 2) return 'AIR';
  return 'GROUND';
};

/** Day-by-day PRBC DOS simulation with injected resupply events. */
const _simulateDOS = (startingStock, dailyConsumption, events, opDuration) => {
  if (!dailyConsumption || dailyConsumption <= 0) {
    return { projection: [], minimumDOS: Infinity, stockoutDay: null };
  }
  const eventByDay = {};
  events.forEach(e => {
    const prbc = _unitsFromModifier(e.quantity_modifier || 1).prbc;
    // BUG-S96-010: stock arrives on arrival_day (when convoy delivers), not dispatch day
    const deliveryDay = e.arrival_day ?? e.day;
    eventByDay[deliveryDay] = (eventByDay[deliveryDay] || 0) + prbc;
  });
  const projection = [];
  let stock = startingStock;
  let minimumDOS = Infinity;
  let stockoutDay = null;
  for (let d = 1; d <= opDuration; d++) {
    const startDOS = stock / dailyConsumption;
    const resupply = eventByDay[d] || 0;
    stock += resupply;
    stock -= dailyConsumption;
    stock  = Math.max(0, stock);
    const endDOS = stock / dailyConsumption;
    if (endDOS < minimumDOS) minimumDOS = endDOS;
    if (stock <= 0 && !stockoutDay) stockoutDay = d;
    projection.push({
      day: d,
      stock_start: startDOS,
      units_received: resupply > 0 ? { prbc: resupply } : null,
      stock_end: endDOS,
      stockout: stock <= 0
    });
  }
  return { projection, minimumDOS: isFinite(minimumDOS) ? minimumDOS : 0, stockoutDay };
};

/**
 * Generate a force-scaled resupply schedule.
 *
 * PKG-002 FIX: nationalStandard param added — targetDOS now resolved from
 *   THRESHOLD_PROFILES[nationalStandard][strategy.thresholdKey] via _resolveTargetDOS().
 *   Romanian CONSERVATIVE = 8 days (not 7), US MINIMAL = 2 days (not 3).
 *
 * PKG-001 FIX: Package type selected based on stockout analysis:
 *   - Blood-only critical items  → EMERGENCY_BLOOD
 *   - Mixed / MASCAL / blood RED → MASS_CASUALTY_PUSH
 *   - Routine resupply           → STANDARD_RESUPPLY
 *
 * Greedy forward simulation:
 *   For each day, if projected DOS will drop below strategy.targetDOS within
 *   safetyMargin days, schedule a resupply on the NEXT day.
 *   Quantity = deficit to restore targetDOS + safetyMargin buffer.
 *   modifier = ceil(neededPRBC / 50) — force-scales the selected package.
 *
 * @param {Object} stockoutAnalysis - { dailyConsumption: { prbc, ffp, plt, cryo }, predictions }
 * @param {Object} supplies         - { prbc_units, ... }
 * @param {Object} scenario         - { operation_parameters: { duration_days }, medical_configuration }
 * @param {string} strategyKey      - 'CONSERVATIVE' | 'BALANCED' | 'MINIMAL'
 * @param {string} nationalStandard - 'NATO' | 'ROMANIAN' | 'US' (default 'NATO')
 * @returns {{ events, summary, dosProjection } | { events: [] }}
 */
export const generateSchedule = (stockoutAnalysis, supplies, scenario, strategyKey = 'BALANCED', nationalStandard = 'NATO') => {
  const strategy         = STRATEGIES[strategyKey] || STRATEGIES.BALANCED;
  const opDuration       = resolveInput(scenario?.operation_parameters?.duration_days, 'duration_days', { zeroIsMissing: true });
  const dailyConsumption = stockoutAnalysis?.dailyConsumption?.prbc || 0;
  const startingStock    = supplies?.prbc_units || 0;

  // PKG-002: Resolve targetDOS from national threshold profile
  // BUG-S96-008: commanded DOS is the floor — auto-schedule must maintain at least that level.
  // Use max(commandedDOS, nationalTarget) so the trigger fires when stock drops below whichever is higher.
  const commandedDOS = scenario?.logistics?.medical_supplies?.commanded_mdos || 0;
  const targetDOS = Math.max(_resolveTargetDOS(strategy, nationalStandard), commandedDOS);

  if (dailyConsumption <= 0 || startingStock <= 0) return { events: [] };

  // PKG-001: Determine appropriate push package based on stockout analysis
  const predictions = stockoutAnalysis?.predictions || [];
  const criticalItems = predictions.filter(p => p.severity === 'CRITICAL');
  const bloodKeys = ['prbc', 'ffp', 'plt', 'cryo', 'PRBC', 'FFP', 'Platelets', 'Cryoprecipitate'];
  const criticalBloodOnly = criticalItems.length > 0 &&
    criticalItems.every(p => bloodKeys.some(k => p.productKey === k || p.product === k));
  const bloodAlertLevel = scenario?.medical_configuration?.blood_config?.alert?.current_level ||
                          scenario?.logistics?.blood_alert_level || 'GREEN';
  const isMASCAL = predictions.some(p => p.severity === 'CRITICAL' && !bloodKeys.some(k => p.productKey === k || p.product === k));

  const _selectPackage = (daysLeft) => {
    if (daysLeft <= strategy.safetyMargin || bloodAlertLevel === 'RED') return 'MASS_CASUALTY_PUSH';
    if (criticalBloodOnly) return 'EMERGENCY_BLOOD';
    if (isMASCAL) return 'MASS_CASUALTY_PUSH';
    return 'STANDARD_RESUPPLY';
  };

  const events     = [];
  let lastEventDay = -99;
  const stockProj  = new Array(opDuration + 2).fill(0);
  stockProj[0]     = startingStock;

  for (let d = 1; d <= opDuration; d++) {
    // BUG-S96-010: stock arrives on arrival_day, not dispatch day — use arrival_day for projection
    const alreadyPRBC = events
      .filter(e => (e.arrival_day ?? e.day) === d)
      .reduce((sum, e) => sum + _unitsFromModifier(e.quantity_modifier || 1).prbc, 0);
    stockProj[d] = Math.max(0, stockProj[d - 1] + alreadyPRBC - dailyConsumption);

    const dosAhead      = (stockProj[d] - dailyConsumption * strategy.safetyMargin) / dailyConsumption;
    const needsResupply = dosAhead < targetDOS;
    const gapOk         = (d - lastEventDay) >= strategy.minEventGap;
    const notLastDay    = d < opDuration;

    if (needsResupply && gapOk && notLastDay) {
      const neededPRBC = Math.max(0, targetDOS * dailyConsumption - stockProj[d]) + dailyConsumption * strategy.safetyMargin;
      const modifier   = Math.max(1, Math.ceil(neededPRBC / STANDARD_RESUPPLY_BASE_PRBC));
      const units      = _unitsFromModifier(modifier);
      const daysLeft   = stockProj[d] / dailyConsumption;

      const deliveryMethod  = _selectDeliveryMethod(daysLeft, strategy.safetyMargin);
      const arrivalDay      = d + 1;
      // BUG-S96-010: compute dispatch day by subtracting transit time.
      // ceil(transitHours / 24) gives full days of lead time needed.
      // dispatchDay is when the convoy must leave; arrivalDay is when stock is available.
      const transitHours    = estimateDeliveryTime('ROLE_3', deliveryMethod);
      const transitDays     = Math.ceil(transitHours / 24);
      const dispatchDay     = Math.max(1, arrivalDay - transitDays);

      const event = {
        day:                 dispatchDay,   // BUG-S96-010: day = DISPATCH day (convoy departs)
        arrival_day:         arrivalDay,    // when stock is available at destination
        transit_hours:       transitHours,
        delivery_method:     deliveryMethod,
        push_package:        _selectPackage(daysLeft),  // PKG-001: context-aware package
        quantity_modifier:   modifier,
        supplies:            {},
        source:              'ROLE_3',
        mtf_target:          'ALL',
        destination_brigade: 'ALL',
        auto_generated:      true,
        products_covered: [
          { product: 'PRBC', units: units.prbc },
          { product: 'FFP',  units: units.ffp  },
          { product: 'PLT',  units: units.plt  }
        ],
        reasoning: `Auto: ${daysLeft.toFixed(1)}d DOS projected, target ${targetDOS}d (${nationalStandard} ${strategy.label})`
      };

      // Gate: dispatch must be within operation; arrival must be within or on last day
      if (dispatchDay >= 1 && arrivalDay <= opDuration + 1) {
        events.push(event);
        lastEventDay = dispatchDay;  // gap tracking by dispatch day
        // Re-simulate remaining days — stock injection happens on arrival_day, not dispatch_day
        for (let dd = arrivalDay; dd <= opDuration; dd++) {
          const injected  = dd === arrivalDay ? units.prbc : 0;
          const otherPRBC = events
            .filter(e => (e.arrival_day ?? e.day) === dd && e !== event)
            .reduce((s, e) => s + _unitsFromModifier(e.quantity_modifier || 1).prbc, 0);
          stockProj[dd] = Math.max(0, stockProj[dd - 1] + injected + otherPRBC - dailyConsumption);
        }
      }
    }
  }

  if (events.length === 0) return { events: [] };

  const withResupply = _simulateDOS(startingStock, dailyConsumption, events, opDuration);
  const totalUnits   = events.reduce(
    (acc, e) => {
      const u = _unitsFromModifier(e.quantity_modifier || 1);
      return { prbc: acc.prbc + u.prbc, ffp: acc.ffp + u.ffp, plt: acc.plt + u.plt, cryo: acc.cryo + u.cryo };
    },
    { prbc: 0, ffp: 0, plt: 0, cryo: 0 }
  );

  return {
    events,
    summary: {
      strategyLabel: strategy.label,
      targetDOS,                    // PKG-002: resolved from national threshold profile
      nationalStandard,             // PKG-001: package selection context
      message:       `${events.length} resupply event${events.length !== 1 ? 's' : ''} scheduled using ${strategy.label} strategy`,
      totalUnits
    },
    dosProjection: {
      minimumDOS:  withResupply.minimumDOS,
      stockoutDay: withResupply.stockoutDay
    }
  };
};

// ============================================================================
// MODULE EXPORTS
// ============================================================================

const SupplyChainModule = {
  calculateSupplyChain,
  calculateLogisticsProjection,
  calculateDailyWeightVolume,  // ENH-010
  calculateConvoyTravelTime,   // ARCH-002
  getSupplyCategory,
  calculateRequiredStock,
  formatG4Request,
  getSupplyStatusColor,
  getSupplyStatusText,
  DOS_THRESHOLDS,
  REQUEST_LEAD_TIMES,
  WASTAGE_FACTORS,
  CONVOY_CAPACITY,             // ENH-010 — re-exported for Tab4/Tab5 display
  // v4.1.0: Resupply scheduler
  generateSchedule,
  STRATEGIES
};

export default SupplyChainModule;
